<script setup lang="ts">
import { useRoute } from '#app'
import {
  get1688GoodsList,
  get1688CategoryTranslationById,
  get1688ProductDetail,
  tbGlobalAddItemsToCart,
} from '@/api'
import { behaviorRecord } from '@/api/behavior.js'
import { getCurrentServer, localeTo1688CountryMap, isTarget_Blank, getMallAppToken } from '@/utils/globalData'
import {
  getGoodsDetailSelectedCountryConfig,
  readGoodsDetailSelectedCountryCache,
  resolveGoodsDetailSelectedCountryValue,
  writeGoodsDetailSelectedCountryCache,
} from '@/utils/goodsDetailSelectedCountry'
import {
  buildMallEstimationQuery,
  resolveEstimateWeightFromAttributeList,
  resolveEstimateWeightFromCandidates,
} from '@/utils/mallShippingEstimate'
import { getCurrentLocaleTransferCode, translateMallBatchTexts } from '@/utils/translate'
import { mallAnalyticsLogEvent } from '@/utils/mallEventLog.js'
import { Decimal } from 'decimal.js'
import { ElLoading } from 'element-plus'
import { getCurrencySymbol, getCurrencyNum } from "@/utils/goods";
import { get } from 'lodash-es'
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue'
import { useMainStore } from '~/composables/store'
import { useCollect } from '~/composables/useCollect'
import { copyText ,$t} from '@/utils/index.js'
import { legacyPathToUserCenterPath } from "~/user-center/routes";
import { queryCountryList } from '@user-center/api/common.ts'

import VueImageZoom from '../common/VueImageZoom.vue'
import RouteBackButton from '../common/RouteBackButton.vue'
import CheckSendAvailabilityCard from '../check-send-package/CheckSendAvailabilityCard.vue'
import DiscountModal from '../common/DiscountModal.vue'
import ProductRecommendationSection from '../common/ProductRecommendationSection.vue'
// import wechat from '@/assets/wechat.png'
// import alipay from '@/assets/alipay.png'
import PurchaseFlowGuide from '@user-center/views/mall/common/PurchaseFlowGuide.vue'
const { locale,  } = useI18n()
// const $t = t
const props = defineProps({
  allArcListMap: {
    type: Object,
    default: () => ({}),
  },
  currentElementIndex: {
    type: Array,
    default: () => [],
  },
  idx: {
    type: Number,
    default: -1,
  },
  parentKeys: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: true,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
})
const lodashGet = get
const store = useMainStore()

const mallUserInfo: any = computed(
  () => get(store, '$state.mallUserInfo') || {},
)
const memberCode = computed(() => {
  const code = get(store, '$state.mallUserInfo.memberCode') || get(store, '$state.userInfo.memberCode') || ''
  return String(code || '').trim()
})
const reportedBehaviorItemIds = new Set<string>()
const reportedMallDetailItemIds = new Set<string>()

const page = ref<any>({})
const router = useRouter()
const route = useRoute()
const currentDetailId = computed(() => String(getRouteId() || '').trim())
function getRouteId() {
  return props.config.isGetRouteId
    ? (route.query && route.query.detailId) || ''
    : props.config.value
}

function tAlibabaDetail(key: string, fallback = '') {
  const fullKey = `alibabaDetail.${key}`
  const value = $t(fullKey)
  return value === fullKey ? fallback : value
}

const detailId = computed(() => currentDetailId.value)

const pageDataProviderKey = computed(() => `detailData_${detailId.value}`)


watch(
  () => route,
  () => {
    nextTick(() => {
      getProductDetail();
    })
  },
  { deep: true }
);
watch(
  () => locale.value,
  () => {
    nextTick(() => {
      getProductDetail()
    })
  },
)
let pageData = null
const injectedPageData = inject(pageDataProviderKey.value, null)
if (injectedPageData) {
  pageData = injectedPageData()
}

if (pageData && pageData.title) {
  page.value = pageData
}

const warehouseList = ref<
  {
    warehouseName: string
    warehouseCode: string
  }[]
>([])

const translatedCategoryInfo = ref<{
  categoryId: string
  categoryName: string
}>({
  categoryId: '',
  categoryName: '',
})

const productInfo = ref<any>({})

type NumericLike = string | number | null | undefined

function toValidNumber(value: NumericLike): number | undefined {
  if (value === null || value === undefined || value === '')
    return undefined

  const normalizedValue = typeof value === 'string' ? value.replace(/,/g, '') : value
  const num = Number(normalizedValue)
  return Number.isFinite(num) ? num : undefined
}

function getAlibabaEffectiveRangePrice(range: any): number {
  return toValidNumber(range?.promotionPrice) ?? toValidNumber(range?.price) ?? 0
}

function getAlibabaDefaultPriceFromSaleInfo(productSaleInfo?: any): number {
  const directPrice = toValidNumber(productSaleInfo?.price)
  const ranges = Array.isArray(productSaleInfo?.priceRangeList)
    ? productSaleInfo.priceRangeList
    : []

  const sortedRanges = ranges
    .map((range: any) => ({
      price: toValidNumber(range?.price),
      promotionPrice: toValidNumber(range?.promotionPrice),
      startQuantity: toValidNumber(range?.startQuantity) ?? 0,
    }))
    .filter((range: any) => range.price !== undefined || range.promotionPrice !== undefined)
    .sort((left: any, right: any) => left.startQuantity - right.startQuantity)

  if (sortedRanges.length === 0)
    return directPrice ?? 0

  const firstRange = sortedRanges[0]
  return firstRange.promotionPrice ?? firstRange.price ?? directPrice ?? 0
}

function getAlibabaCartUnitPrice(item: any, fallbackPrice = 0): number {
  return (
    toValidNumber(item?.promotionPrice)
    ?? toValidNumber(item?.price)
    ?? toValidNumber(item?.consignPrice)
    ?? fallbackPrice
  )
}

const productSkuInfos = computed(() => {
  if (!productInfo.value.productSkuInfos && productInfo.value.offerId) {
    const offerId = productInfo.value.offerId
    const price = getAlibabaDefaultPriceFromSaleInfo(productInfo.value.productSaleInfo)
    const amountOnSale = productInfo.value.productSaleInfo?.amountOnSale
    return [
      {
        skuAttributes: [
          {
            attributeId: offerId,
            attributeName: $t('texts.buyAmount'),
            value: '-',
          },
        ],
        specId: null,
        skuId: offerId,
        amountOnSale,
        price,
      },
    ]
  }
  return productInfo.value.productSkuInfos
})
const productImages = computed(() => {
  return productInfo.value.productImage?.images.map((img: string) => img) || []
})

function buildAlibabaMallEventParams(extra: Record<string, any> = {}) {
  const firstSku = buyList.value?.[0] || productSkuInfos.value?.[0] || {}
  return {
    screenName: '/alibaba/goods',
    pageCode: 'product_detail',
    platform: 'alibaba',
    orderSource: 2,
    itemId: productInfo.value.offerId || detailId.value,
    itemSkuId: firstSku.specId || firstSku.skuId || productInfo.value.offerId,
    quantity: buyTotalQuantity.value || summaryDisplayQuantity.value,
    shopId: productInfo.value.sellerOpenId,
    shopNick: productInfo.value.sellerLoginId,
    title: productInfo.value.subject,
    price: multiply(getAlibabaCartUnitPrice(firstSku, rangePrice.value || 0), 100),
    amountUnit: 'minor',
    ...extra,
  }
}

function reportAlibabaMallDetailView() {
  const offerId = String(productInfo.value.offerId || detailId.value || '').trim()
  if (!offerId || reportedMallDetailItemIds.has(offerId)) return
  reportedMallDetailItemIds.add(offerId)
  mallAnalyticsLogEvent('alibaba_goods_detail_view', buildAlibabaMallEventParams())
}
const heroThumbImages = computed(() => {
  return (productImages.value || []).slice(0, 5)
})
const skuPreviewImages = computed(() => {
  const imageSet = new Set<string>()

    ; (productSpecs.value || []).forEach((spec: any) => {
      ; (spec.attributeValues || []).forEach((attrValue: any) => {
        const imageUrl = String(attrValue?.skuImageUrl || '').trim()
        if (imageUrl) {
          imageSet.add(imageUrl)
        }
      })
    })

  return Array.from(imageSet)
})

const productImg = computed(() => {

  return productImages.value[slideIndex.value] || (heroThumbImages.value && heroThumbImages.value[0])
}) 

const productAttributes = computed(() => {
  const attrMap = new Map()
  productInfo.value.productAttribute?.forEach((attr: any) => {
    const { attributeName, value, valueTrans } = attr
    if (!attrMap.has(attributeName)) {
      attrMap.set(attributeName, {
        ...attr,
        values: [value],
        valuesTrans: [valueTrans],
      })
    }
    else {
      attrMap.get(attributeName).values.push(value)
      attrMap.get(attributeName).valuesTrans.push(valueTrans)
    }
  })

  const attrList = Array.from(attrMap.values())
  if (attrList.length % 2 !== 0) {
    attrList.push({
      attributeName: '',
      values: [],
      valuesTrans: [],
    })
  }
  return attrList
})
const productSpecs = computed(() => {
  const specMap = new Map()

  productSkuInfos.value?.forEach((sku: any) => {
    sku.skuAttributes.forEach((attr: any) => {
      const {
        attributeName,
        attributeNameTrans,
        attributeId,
        value,
        valueTrans,
        skuImageUrl,
      } = attr

      if (!specMap.has(attributeId)) {
        specMap.set(attributeId, {
          attributeId,
          attributeName,
          attributeNameTrans,
          attributeValues: new Map(),

        })
      }

      const spec = specMap.get(attributeId)
      if (!spec.attributeValues.has(value)) {
        spec.attributeValues.set(value, {
          value,
          valueTrans,
          skuImageUrl: skuImageUrl || null,
        })
      }
    })
  })

  return Array.from(specMap.values()).map(spec => ({
    ...spec,
    attributeValues: Array.from(spec.attributeValues.values()),
  }))
})

const isTranslatedLocale = computed(() => {
  return !!localeTo1688CountryMap[locale.value]
})

const current1688Country = computed(() => {
  return localeTo1688CountryMap[locale.value] || 'en'
})

const sellerDisplayName = computed(() => {
  return getTranslatedDetailText(productInfo.value.sellerLoginId) || "--"
})

const shippingOriginText = computed(() => {
  return getTranslatedDetailText(productInfo.value.productShippingInfo?.sendGoodsAddressText) || "--"
})

const supplierRating = computed(() => {
  const score = Number(
    get(productInfo.value, 'sellerDataInfo.compositeServiceScore')
    || get(productInfo.value, 'sellerDataInfo.sellerRating')
    || 0
  )
  return Number.isFinite(score) && score > 0 ? score : 0
})

const supplierStarCount = computed(() => {
  return Math.min(5, Math.max(0, Math.round(supplierRating.value)))
})

const supplierRatingText = computed(() => {
  const score = supplierRating.value
  return score > 0 ? score.toFixed(1) : '--'
})

const supplierTypeText = computed(() => {
  const type =
    get(productInfo.value, 'sellerDataInfo.supplierType')
    || get(productInfo.value, 'supplierType')
    || ''
  if (type) return getTranslatedDetailText(type) || type
  return tAlibabaDetail('superFactory', '超級工廠')
})

const supplierTransactionText = computed(() => {
  const count =
    get(productInfo.value, 'sellerDataInfo.transactionCount')
    || get(productInfo.value, 'sellerDataInfo.tradeQuantity')
    || get(productInfo.value, 'tradeQuantity')
  if (count !== undefined && count !== null && count !== '') {
    const num = Number(count)
    if (Number.isFinite(num)) {
      if (num >= 10000) return (num / 10000).toFixed(1).replace(/\.0$/, '') + 'w+'
      if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k+'
      return String(Math.floor(num))
    }
    return String(count)
  }
  return '--'
})

const supplierRepurchaseRate = computed(() => {
  const rate =
    get(productInfo.value, 'sellerDataInfo.repurchaseRate')
    || get(productInfo.value, 'sellerDataInfo.repeatPurchaseRate')
    || get(productInfo.value, 'repeatPurchaseRate')
  if (rate !== undefined && rate !== null && rate !== '') {
    const num = Number(rate)
    if (Number.isFinite(num)) {
      return num > 1 ? num.toFixed(1) + '%' : (num * 100).toFixed(1) + '%'
    }
    return String(rate)
  }
  return '--'
})

function generateFixedRating(offset = 0) {
  const id = String(productInfo.value?.sellerOpenId || productInfo.value?.offerId || detailId.value || '')
  const seed = `${id}_${offset}`
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  const random = Math.abs(hash) / 2147483647
  return Math.round((4.5 + random * 0.5) * 10) / 10
}

const mainRating = computed(() => generateFixedRating(0))

const qualityRating = computed(() => generateFixedRating(1))

const shippingRating = computed(() => generateFixedRating(2))

const serviceRating = computed(() => generateFixedRating(3))

function getRatingLevel(score) {
  const n = Number(score)
  if (n > 4.7) return { class: 'high', text: $t('tbDetail.high') }
  if (n >= 4.4) return { class: 'mid', text: $t('tbDetail.mid') }
  return { class: 'low', text: $t('tbDetail.low') }
}

const supplierCertification = computed(() => {
  const cert =
    get(productInfo.value, 'sellerDataInfo.certifications')
    || get(productInfo.value, 'sellerDataInfo.certification')
    || get(productInfo.value, 'certification')
  if (cert) {
    if (Array.isArray(cert)) return cert.join(' / ')
    return String(cert)
  }
  return '--'
})

const supplierTradeLevel = computed(() => {
  const level =
    get(productInfo.value, 'sellerDataInfo.tradeMedalLevel')
    || get(productInfo.value, 'tradeMedalLevel')
  return level !== undefined && level !== null ? String(level) : ''
})

const supplierFactoryImages = computed(() => {
  const images =
    get(productInfo.value, 'sellerDataInfo.factoryImages')
    || get(productInfo.value, 'factoryImages')
    || get(productInfo.value, 'sellerFactoryImages')
  if (Array.isArray(images)) return images.filter(Boolean)
  return []
})

const productSubjectText = computed(() => {
  if (isTranslatedLocale.value) {
    return productInfo.value.subjectTrans || productInfo.value.subject || ""
  }
  return productInfo.value.subject || ""
})

const productUnitText = computed(() => {
  const unitInfo = productInfo.value.productSaleInfo?.unitInfo || {}
  if (isTranslatedLocale.value && unitInfo.transUnit) {
    return unitInfo.transUnit
  }
  return unitInfo.unit || ""
})

const productSummaryText = computed(() => {
  const summaryParts = [
    shippingOriginText.value !== "--" ? shippingOriginText.value : "",
    productInfo.value.productShippingInfo?.averageDeliveryTime || "",
  ].filter(Boolean)

  if (summaryParts.length) {
    return summaryParts.join(" · ")
  }

  return tAlibabaDetail("summaryFallback", "精选1688优质货源，支持多规格采购与仓储代发")
})

const priceRangeList = computed(() => {
  return [...(productInfo.value.productSaleInfo?.priceRangeList || [])].sort(
    (left: any, right: any) => Number(left.startQuantity || 0) - Number(right.startQuantity || 0),
  )
})

const heroPriceCards = computed(() => {
  if (priceRangeList.value.length > 0) {
    return priceRangeList.value.map((item: any, index: number) => ({
      price: getAlibabaEffectiveRangePrice(item),
      label: getPriceRangeLabel(index),
    }))
  }

  const singlePrice = getAlibabaDefaultPriceFromSaleInfo(productInfo.value.productSaleInfo) || rangePrice.value || 0
  const minQuantity = productInfo.value.minOrderQuantity || 1
  return [
    {
      price: singlePrice,
      label: `≥${minQuantity}${productUnitText.value || tAlibabaDetail("pieceUnit", "件")}`,
    },
  ]
})

const selectionSpecs = computed(() => {
  return (productSpecs.value || []).slice(0, -1)
})

const quantitySpec = computed(() => {
  const specs = productSpecs.value || []
  return specs.length ? specs[specs.length - 1] : null
})

const quantityRows = computed(() => {
  return quantitySpec.value?.attributeValues || []
})
const showAllQuantityRows = ref(false)
const previewQuantityRows = computed(() => {
  return showAllQuantityRows.value ? quantityRows.value : quantityRows.value.slice(0, 3)
})
const hasMoreQuantityRows = computed(() => {
  return quantityRows.value.length > 3
})

const originalProductUrl = computed(() => {
  if (!productInfo.value.offerId) {
    return ""
  }
  return `https://detail.1688.com/offer/${productInfo.value.offerId}.html`
})

const parameterPreviewItems = computed(() => {
  return (productAttributes.value || []).filter((item: any) => item.attributeName).slice(0, 4)
})

const editorialFeatureCards = computed(() => {
  const images = productImages.value || []
  const attrs = parameterPreviewItems.value || []

  return [
    {
      title: tAlibabaDetail("featureDetailTitle", "细节展示"),
      desc:
        getAttributeDisplayValue(attrs[0])
        || productSummaryText.value
        || tAlibabaDetail("featureDetailDesc", "支持查看商品材质、颜色与做工细节"),
      image: images[1] || images[0] || "",
    },
    {
      title: tAlibabaDetail("featureSceneTitle", "场景参考"),
      desc:
        getAttributeDisplayValue(attrs[1])
        || tAlibabaDetail("featureSceneDesc", "结合实拍图快速了解商品展示效果与使用氛围"),
      image: images[2] || images[1] || images[0] || "",
    },
  ].filter(item => item.image)
})

const editorialCallout = computed(() => {
  return {
    title: tAlibabaDetail("calloutTitle", "验货与仓配保障"),
    desc: tAlibabaDetail("calloutDesc", "下单后可进行验货、仓储与合单发货，我们会在国际运输前协助核对商品状态与包装完整度。"),
  }
})

const disclaimerPopupDescParts = computed(() => {
  const text = tAlibabaDetail('disclaimerPopupDesc',
    '本平台為代購服務平台，商品均由第三方商家提供並直接發貨。平台僅協助交易撮合及售後服務，不對商品的品質、真偽及發貨時效承擔法律責任。請您在下單前仔細甄別商品信息，若有疑問可聯繫客服諮詢。')
  const match = text.match(/^([^。.]+[。.])\s*(.+)$/s)
  return match ? { part1: match[1], part2: match[2] } : { part1: text, part2: '' }
})

const platformDisclaimerParts = computed(() => {
  const text = tAlibabaDetail('platformDisclaimer',
    '本平台為代購服務平台，商品均由第三方商家提供並直接發貨。平台僅協助交易撮合及售後服務，不對商品的品質、真偽及發貨時效承擔法律責任。請您在下單前仔細甄別商品信息，若有疑問可聯繫客服諮詢。')
  const match = text.match(/^([^。.]+[。.])\s*(.+)$/s)
  return match ? { part1: match[1], part2: match[2] } : { part1: text, part2: '' }
})

const heroInfoItems = computed(() => {
  return [
    {
      label: tAlibabaDetail("shipFrom", "发货地区"),
      value: shippingOriginText.value,
    },
    {
      label: $t("texts.averageDeliveryTime"),
      value: productInfo.value.productShippingInfo?.averageDeliveryTime || "--",
    },
    {
      label: tAlibabaDetail("minOrderQty", "起订量"),
      value: `${productInfo.value.minOrderQuantity || "--"} ${productUnitText.value || ""}`.trim(),
    },
  ]
})

const summaryDisplayQuantity = computed(() => {
  return buyTotalQuantity.value || Number(productInfo.value.minOrderQuantity || 0) || 1
})

const summaryDisplayPrice = computed(() => {
  if (buyTotalQuantity.value > 0) {
    return buyTotalPrice.value
  }

  const basePrice = Number(rangePrice.value || getAlibabaDefaultPriceFromSaleInfo(productInfo.value.productSaleInfo) || 0)
  return multiply(basePrice, summaryDisplayQuantity.value)
})

const parameterItems = computed(() => {
  return (productAttributes.value || []).filter((item: any) => item.attributeName)
})

const logisticsOriginText = computed(() => {
  return shippingOriginText.value
})

const domesticFreightText = computed(() => {
  return formatDisplayMoney(0)
})

const internationalFreightEstimateText = computed(() => {
  return tAlibabaDetail('freightEstimate', '运费估算')
})

const internationalFreightAmountText = computed(() => {
  return `${formatDisplayPrice(300)} ${tAlibabaDetail('freightEstimateSuffix', '起')}`
})

const selectedAddress = ref('')
const addressOptions = ref([])
const countryListCache = ref([])
const cachedSelectedCountry = ref('')
const currentStoreInfo = computed(() => {
  return store.getStoreInfoDataMap(getCurrentServer()) || {}
})
const configuredSelectedCountry = computed(() => {
  return getGoodsDetailSelectedCountryConfig(currentStoreInfo.value)
})
const preferredSelectedCountry = computed(() => {
  return String(cachedSelectedCountry.value || configuredSelectedCountry.value || '').trim()
})

function syncSelectedAddressByPreference(options: { forcePreferred?: boolean } = {}) {
  const list = Array.isArray(countryListCache.value) ? countryListCache.value : []
  if (!list.length) {
    return
  }

  const selected = String(selectedAddress.value || '').trim()
  const hasSelected = list.some(item => String(item?.value || '').trim() === selected)
  const preferredValue = resolveGoodsDetailSelectedCountryValue(list, preferredSelectedCountry.value)

  if (preferredValue && (options.forcePreferred || !hasSelected)) {
    selectedAddress.value = preferredValue
    return
  }

  if (!hasSelected) {
    selectedAddress.value = String(list[0]?.value || '').trim()
  }
}

function handleSelectedAddressChange(value: string) {
  const normalizedValue = resolveGoodsDetailSelectedCountryValue(countryListCache.value, value)
  if (!normalizedValue) {
    return
  }

  selectedAddress.value = normalizedValue
  cachedSelectedCountry.value = writeGoodsDetailSelectedCountryCache(normalizedValue)
}

const checkSendCountryCode = computed(() => {
  const selected = String(selectedAddress.value || '').trim()
  if (selected) {
    const matchedCountry = countryListCache.value.find(item => String(item?.value || '').trim() === selected)
    if (matchedCountry?.value) {
      return String(matchedCountry.value).trim()
    }
  }

  return String(countryListCache.value[0]?.value || '').trim()
})

const checkSendGoodType = computed(() => {
  return String(translatedCategoryInfo.value.categoryName || productInfo.value?.categoryName || '').trim()
})

const checkSendDisplayCategories = computed(() => {
  const name = String(translatedCategoryInfo.value.categoryName || productInfo.value?.categoryName || '').trim()
  return name ? [name] : []
})

const estimationWeight = computed(() => {
  const defaultSku = Array.isArray(productSkuInfos.value) && productSkuInfos.value.length
    ? productSkuInfos.value[0]
    : null

  return resolveEstimateWeightFromCandidates([
    {
      value: get(productInfo.value, 'productShippingInfo.weight'),
      unit: get(productInfo.value, 'productShippingInfo.weightUnit'),
    },
    get(productInfo.value, 'productShippingInfo.weightText'),
    {
      value: get(productInfo.value, 'weight'),
      unit: get(productInfo.value, 'weightUnit'),
    },
    {
      value: get(productInfo.value, 'itemWeight'),
      unit: get(productInfo.value, 'weightUnit'),
    },
    {
      value: get(productInfo.value, 'grossWeight'),
      unit: get(productInfo.value, 'weightUnit'),
    },
    get(productInfo.value, 'packageWeight'),
    get(productInfo.value, 'shippingWeight'),
    get(productInfo.value, 'weightText'),
    {
      value: get(defaultSku, 'weight'),
      unit: get(defaultSku, 'weightUnit'),
    },
    {
      value: get(defaultSku, 'itemWeight'),
      unit: get(defaultSku, 'weightUnit'),
    },
    {
      value: get(defaultSku, 'grossWeight'),
      unit: get(defaultSku, 'weightUnit'),
    },
    get(defaultSku, 'skuWeight'),
    get(defaultSku, 'weightText'),
  ])
    || resolveEstimateWeightFromAttributeList(get(productInfo.value, 'productAttribute', []))
    || '1'
})

// const paymentIcons = {
//   wechatPay: wechat,
//   alipay: alipay
// }
const showDisclaimerPopup = ref(false)
const showFreightTooltip = ref(false)
const showConsultTooltip = ref(false)
const showAssistantPopup = ref(false)
const showSharePopup = ref(false)
const showCartSuccessModal = ref(false)
const showDiscountModal = ref(false)
const agreementChecked = ref(true)

function handleToggleDisclaimerPopup() {
  showDisclaimerPopup.value = !showDisclaimerPopup.value
  if (showDisclaimerPopup.value) {
    showAssistantPopup.value = false
    showSharePopup.value = false
  }
}

function handleToggleAssistantPopup() {
  showAssistantPopup.value = !showAssistantPopup.value
  if (showAssistantPopup.value) {
    showDisclaimerPopup.value = false
    showSharePopup.value = false
  }
}

function handleToggleSharePopup() {
  showSharePopup.value = !showSharePopup.value
  if (showSharePopup.value) {
    showDisclaimerPopup.value = false
    showAssistantPopup.value = false
  }
}

function getFreightEstimateQuery() {
  return buildMallEstimationQuery({
    countryCode: checkSendCountryCode.value,
    weight: estimationWeight.value,
    fallbackWeight: 1,
  })
}

function getFreightEstimateUrl() {
  const query = getFreightEstimateQuery()
  const params = new URLSearchParams(query)

  return params.toString()
    ? `/collection/estimation?${params.toString()}`
    : '/collection/estimation'
}

function openFreightEstimate() {
  if (!process.client) return
  window.open(getFreightEstimateUrl(), '_blank')
}

function goFreightEstimate() {
  router.push({
    path: '/collection/estimation',
    query: getFreightEstimateQuery(),
  })
  showAssistantPopup.value = false
}

function goContactUs() {
  router.push('/v?url=contact_us')
}

function goTransshipment() {
  router.push('/user-center/collection/myWarehouse')
  showAssistantPopup.value = false
}

watch(() => shippingOriginText.value, (origin) => {
  addressOptions.value = []
  // if (origin) {
  //   addressOptions.value.push({ value: origin, label: origin })
  // }
  if (countryListCache.value.length > 0) {
    addressOptions.value.push(...countryListCache.value)
  }

  const shopId = useMallShopId().getShopId()
  if (shopId && countryListCache.value.length === 0) {
    queryCountryList({ dealerShopId:shopId }).then((res) => {
      const list = res.data || []
      countryListCache.value = list.map((c) => ({
        value: c.code,
        label: c.aliasName || c.name || c.code,
        code: c.code,
        aliasName: c.aliasName,
        name: c.name,
        country: c.country,
      }))
      addressOptions.value = [...countryListCache.value]
    })
  }
}, { immediate: true })

watch(countryListCache, (list) => {
  if (!Array.isArray(list) || !list.length) {
    return
  }

  addressOptions.value = [...list]
  syncSelectedAddressByPreference()
}, { deep: true })

watch(preferredSelectedCountry, () => {
  syncSelectedAddressByPreference({ forcePreferred: true })
}, { immediate: true })

async function syncTranslatedCategoryInfo() {
  const rawCategoryId = productInfo.value?.categoryId
    || productInfo.value?.productCategoryId
    || productInfo.value?.leafCategoryId
    || productInfo.value?.categoryNo
  const rawCategoryName = String(productInfo.value?.categoryName || '').trim()
  const categoryId = String(rawCategoryId || '').trim()

  translatedCategoryInfo.value = {
    categoryId,
    categoryName: rawCategoryName,
  }

  if (!categoryId) {
    return
  }

  try {
    const response = await get1688CategoryTranslationById({
      param: {
        categoryId,
        country: current1688Country.value,
        language: current1688Country.value,
      },
      headers: {
        language: current1688Country.value,
      },
    })
    const resolvedCategoryName = String(
      get(response, 'data.chineseName')
      || get(response, 'data.data.chineseName')
      || rawCategoryName
      || get(response, 'data.categoryName')
      || get(response, 'data.data.categoryName')
      || get(response, 'data.name')
      || get(response, 'data.data.name')
      || get(response, 'data.translatedName')
      || get(response, 'data.data.translatedName')
      || get(response, 'data.categoryNameTrans')
      || get(response, 'data.data.categoryNameTrans')
      || '',
    ).trim()

    if (resolvedCategoryName) {
      translatedCategoryInfo.value = {
        categoryId,
        categoryName: resolvedCategoryName,
      }
      return
    }
  }
  catch (error) {
    console.error('1688 category translation by id failed:', error)
  }

  const targetLanguage = getCurrentLocaleTransferCode(locale.value)
  if (!targetLanguage || !rawCategoryName) {
    return
  }

  try {
    const { translatedMap } = await translateMallBatchTexts({
      texts: [rawCategoryName],
      targetLanguage: locale.value,
    })
    translatedCategoryInfo.value = {
      categoryId,
      categoryName: String(translatedMap?.[rawCategoryName] || rawCategoryName).trim(),
    }
  }
  catch (error) {
    console.error('1688 category translate fallback failed:', error)
  }
}

function getPriceRangeLabel(index: number) {
  const current = priceRangeList.value[index]
  if (!current) {
    return ""
  }

  const next = priceRangeList.value[index + 1]
  const unitText = productUnitText.value || tAlibabaDetail("pieceUnit", "件")

  if (!next) {
    return `≥${current.startQuantity}${unitText}`
  }

  return `${current.startQuantity}-${Number(next.startQuantity) - 1}${unitText}`
}

const slideIndex = ref(0)
const vueImageZoomOptions = computed(() => {
  console.log(productImg.value,'====')
  return {
    height: 450,
    // width:287,
    img: productImg.value,
    zoomStyle: 'height: 450px; border-radius: 12px; z-index: 9;',
    scale: 1,
    offset: { vertical: 0, horizontal: 14 },
    zoomPosition: 'right',
  }
})

const productError = ref(false)
const translatedDetailTextMap = ref<Record<string, string>>({})
const latestDetailTranslateId = ref(0)

function normalize1688GoodsListResponse(responseData: any) {
  const rawList
    = lodashGet(responseData, 'data.dataList')
    ?? lodashGet(responseData, 'data.data')
    ?? []
  const list = Array.isArray(rawList) ? rawList : []

  return list.map((item: any) => ({
    ...item,
    id: item.offerId,
    imageUrl: item.imageUrl,
    title: isTranslatedLocale.value ? item.subjectTrans || item.subject : item.subject,
    finalPrice: item.priceInfo?.price,
  }))
}

function getRecommendPrice(item: any) {
  const numeric = Number(item?.finalPrice ?? item?.transferPrice ?? item?.priceInfo?.price ?? item?.price ?? 0)
  return Number.isFinite(numeric) ? numeric : 0
}

function getRecommendLink(item: any) {
  const offerId = String(item?.offerId || item?.id || '').trim()
  return offerId ? `/product?detailId=${encodeURIComponent(offerId)}` : '#'
}

function getRecommendResponseTotal(response: any) {
  const total = Number(
    lodashGet(response, 'data.totalRecord')
    ?? lodashGet(response, 'data.data.totalRecord')
    ?? lodashGet(response, 'data.total')
    ?? 0,
  )
  return Number.isFinite(total) && total > 0 ? total : 0
}

const recommendReloadKey = computed(() => [
  String(productInfo.value.offerId || '').trim(),
  String(productInfo.value.categoryId || '').trim(),
  current1688Country.value,
  locale.value,
].join('|'))

function format1688RecommendationPrice(item: any) {
  return formatDisplayMoney(getRecommendPrice(item))
}

async function fetch1688RecommendationPage({ page, pageSize }: { page: number, pageSize: number }) {
  const offerId = String(productInfo.value.offerId || '').trim()
  if (!offerId) return { items: [], hasMore: false }

  const baseParam: Record<string, any> = {
    pageSize,
    beginPage: page,
    country: current1688Country.value,
    sort: JSON.stringify({ monthSold: 'desc' }),
  }
  const candidateParams: Record<string, any>[] = []
  const categoryId = productInfo.value.categoryId
  const keyword = String(productInfo.value.subject || productInfo.value.subjectTrans || '').trim()

  if (categoryId) {
    candidateParams.push({
      ...baseParam,
      categoryId,
      keyword: keyword || undefined,
    })
  }

  if (keyword) {
    candidateParams.push({
      ...baseParam,
      keyword,
    })
  }

  let lastError: unknown = null
  for (const param of candidateParams) {
    try {
      const response = await get1688GoodsList({ param })
      const items = normalize1688GoodsListResponse(response)

      if (items.length) {
        return {
          items,
          total: getRecommendResponseTotal(response),
        }
      }
    }
    catch (error) {
      lastError = error
    }
  }

  if (lastError) throw lastError
  return { items: [], hasMore: false }
}

function getTranslatedDetailText(rawText: any) {
  const text = String(rawText || '').trim()
  if (!text)
    return ''
  return productInfo.value.transfersMap?.[text]
    || translatedDetailTextMap.value[text]
    || text
}

async function translateDetailMetaTexts() {
  const targetLanguage = getCurrentLocaleTransferCode(locale.value)
  if (!targetLanguage) {
    translatedDetailTextMap.value = {}
    return
  }

  const existingMap = productInfo.value.transfersMap || {}
  const texts = [
    String(productInfo.value.sellerLoginId || '').trim(),
    String(productInfo.value.productShippingInfo?.sendGoodsAddressText || '').trim(),
  ]
    .filter(Boolean)
    .filter(text => !existingMap[text])

  if (!texts.length) {
    translatedDetailTextMap.value = {}
    return
  }

  const translateId = ++latestDetailTranslateId.value
  const { translatedMap } = await translateMallBatchTexts({
    texts,
    targetLanguage: locale.value,
  })

  if (translateId !== latestDetailTranslateId.value)
    return

  translatedDetailTextMap.value = translatedMap || {}
}

async function getProductDetail() {
  const loadingInstance = ElLoading.service({
    lock: true,
    text: 'Loading',
    background: 'rgba(255, 255, 255, 0.5)',
  })

  try {
    const res = await get1688ProductDetail({
      param: {
        country: current1688Country.value,
        offerId: detailId.value,
      },
    })
    productInfo.value = res.data
    reportBrowseBehaviorRecord()
    reportAlibabaMallDetailView()
    await syncTranslatedCategoryInfo()
    slideIndex.value = 0
    generateSkuMap()
    showAllQuantityRows.value = false
    productError.value = false
    await translateDetailMetaTexts()
  }
  catch (error) {
    productError.value = true
    translatedDetailTextMap.value = {}
    console.error(error)
  }
  finally {
    loadingInstance.close()
  }
}

const skuMap = ref<{
  [skuId: string]: {
    buyQuantity: number
    [key: string]: any
  }
}>({})
function generateSkuMap() {
  const result: {
    [skuId: string]: {
      buyQuantity: number
      [key: string]: any
    }
  } = {}
  productSkuInfos.value?.forEach((sku: any) => {
    result[sku.skuId] = {
      ...sku,
      buyQuantity: 0,
    }
  })
  skuMap.value = result
  selectedAttributes.value = {}

  const defaultSku = productSkuInfos.value?.[0]
  const defaultSkuAttributes = defaultSku?.skuAttributes || []
  if (!defaultSkuAttributes.length)
    return

  const len = defaultSkuAttributes.length
  defaultSkuAttributes.forEach((attr: any, index: number) => {
    if (index !== len - 1) {
      handleSelectSpecValue(attr.attributeId, attr)
    }
  })
}

const attrValueBuyQuantityMap = computed(() => {
  const result: {
    [skuOnlyValue: string]: number
  } = {}
  Object.keys(skuMap.value).forEach((skuId) => {
    const sku = skuMap.value[skuId]
    sku.skuAttributes.forEach((attr: any) => {
      if (result[`${attr.attributeId},${attr.value}`]) {
        result[`${attr.attributeId},${attr.value}`] += sku.buyQuantity
      }
      else {
        result[`${attr.attributeId},${attr.value}`] = sku.buyQuantity
      }
    })
  })
  return result
})

const selectedAttributes = ref<any>({})
function handleSelectSpecValue(attributeId: string, attrValue: any) {
  selectedAttributes.value = {
    ...selectedAttributes.value,
    [attributeId]: attrValue.value,
  }
}

function getSkuId(attributeId: string, value: string) {
  const _selectedAttributes: {
    [key: string]: string
  } = {
    ...selectedAttributes.value,
    [attributeId]: value,
  }
  const sku = productSkuInfos.value?.find((sku: any) =>
    sku.skuAttributes.every(
      (attr: any) => _selectedAttributes[attr.attributeId] === attr.value,
    ),
  )
  return sku?.skuId
}

function attrValueIsSelected(attributeId: string, value: string) {
  return selectedAttributes.value[attributeId] === value
}

function getSpecDisplayName(spec: any) {
  if (isTranslatedLocale.value && spec?.attributeNameTrans) {
    return spec.attributeNameTrans
  }
  return spec?.attributeName || ""
}

function getSpecDisplayValue(attrValue: any) {
  if (isTranslatedLocale.value && attrValue?.valueTrans) {
    return attrValue.valueTrans
  }
  return attrValue?.value || ""
}

function getSkuAttributeDisplayValue(attrValue: any) {
  if (!attrValue) {
    return ""
  }
  if (isTranslatedLocale.value && attrValue?.valueTrans) {
    return attrValue.valueTrans
  }
  return attrValue?.value || ""
}

function getAttributeDisplayName(attr: any) {
  if (isTranslatedLocale.value && attr?.attributeNameTrans) {
    return attr.attributeNameTrans
  }
  return attr?.attributeName || ""
}

function getAttributeDisplayValue(attr: any) {
  if (isTranslatedLocale.value && Array.isArray(attr?.valuesTrans) && attr.valuesTrans.length) {
    return attr.valuesTrans.join(", ")
  }
  return Array.isArray(attr?.values) ? attr.values.join(", ") : ""
}

function getSkuInfoByAttrValue(attributeId: string, value: string) {
  const skuId = getSkuId(attributeId, value)
  if (!skuId) {
    return undefined
  }
  return skuMap.value[skuId]
}

function trimPriceText(raw: any) {
  const text = String(raw ?? "")

  if (!text.includes(".")) {
    return text
  }

  return text
    .replace(/0+$/, "")
    .replace(/\.$/, "")
}

function formatDisplayPrice(value: any) {
  const numeric = Number(value ?? 0)
  if (!Number.isFinite(numeric)) {
    return "0"
  }

  return trimPriceText(getCurrencyNum(numeric))
}

function formatDisplayMoney(value: any) {
  return `${getCurrencySymbol("¥")} ${formatDisplayPrice(value)}`
}

function updateSkuQuantity(skuId: string, delta: number) {
  const targetSku = skuMap.value[skuId]
  if (!targetSku) {
    return
  }

  const current = Number(targetSku.buyQuantity || 0)
  const max = Number(targetSku.amountOnSale || 999999)
  const next = Math.max(0, Math.min(max, current + delta))
  targetSku.buyQuantity = next
}

function setSkuQuantity(skuId: string, rawValue: any) {
  const targetSku = skuMap.value[skuId]
  if (!targetSku) {
    return
  }

  const parsed = Number.parseInt(String(rawValue || "0"), 10)
  const nextValue = Number.isFinite(parsed) ? parsed : 0
  const max = Number(targetSku.amountOnSale || 999999)
  targetSku.buyQuantity = Math.max(0, Math.min(max, nextValue))
}

function handleSkuQuantityInput(skuId: string, event: Event) {
  const target = event.target as HTMLInputElement | null
  setSkuQuantity(skuId, target?.value || 0)
}

function getSkuPreviewImageIndex(imageUrl: any) {
  const currentUrl = String(imageUrl || '').trim()
  if (!currentUrl) {
    return 0
  }
  const index = skuPreviewImages.value.findIndex(item => item === currentUrl)
  return index >= 0 ? index : 0
}

const totalQuantity = computed(() => {
  const buyList = Object.values(skuMap.value).filter(
    item => item.buyQuantity > 0,
  )
  return buyList.reduce((total, item) => total + item.buyQuantity, 0)
})

/**
 * 1688 当前数量区间价格
 */
const rangePrice = computed(() => {
  const quantity = totalQuantity.value;
  // 安全获取价格区间列表并创建副本
  const priceRangeList = [...(productInfo.value.productSaleInfo?.priceRangeList || [])];

  // 如果没有价格区间数据，返回0或您认为合适的默认值
  if (priceRangeList.length === 0) return 0;

  // 将区间按照起始数量从小到大排序
  priceRangeList.sort((a, b) => a.startQuantity - b.startQuantity);

  // 特殊情况：数量为0时返回最低区间的价格
  if (quantity === 0) return getAlibabaEffectiveRangePrice(priceRangeList[0]);

  // 从高到低检查区间
  for (let i = priceRangeList.length - 1; i >= 0; i--) {
    if (quantity >= priceRangeList[i].startQuantity) {
      return getAlibabaEffectiveRangePrice(priceRangeList[i]);
    }
  }

  // 如果没有匹配任何区间，返回最低区间的价格
  return getAlibabaEffectiveRangePrice(priceRangeList[0]);
});

const buyList = computed(() => {
  return Object.values(skuMap.value).filter(item => item.buyQuantity > 0)
})

const buyFirstAttrValueGroupList = computed(() => {
  const firstAttrValueGroupMap = new Map()

  buyList.value.forEach((item) => {
    const firstAttrValue = getSkuAttributeDisplayValue(item.skuAttributes[0])
    const skuStr = item.skuAttributes
      .slice(1)
      .map((attrItem: any) => getSkuAttributeDisplayValue(attrItem))
      .filter(Boolean)
      .join(' ')
    if (firstAttrValueGroupMap.has(firstAttrValue)) {
      const itemMap = firstAttrValueGroupMap.get(firstAttrValue)
      itemMap.value.push({
        ...item,
        skuStr,
        firstAttrValue,
      })
      itemMap.totalQuantity = add(itemMap.totalQuantity, item.buyQuantity)
    }
    else {
      firstAttrValueGroupMap.set(firstAttrValue, {
        key: firstAttrValue,
        value: [
          {
            ...item,
            skuStr,
            firstAttrValue,
          },
        ],
        totalQuantity: item.buyQuantity,
      })
    }
  })

  return Array.from(firstAttrValueGroupMap.values())
})

const showBuyDetailList = ref(false)

function handleChangeShowBuyDetailList() {
  showBuyDetailList.value = !showBuyDetailList.value
}

function handleToggleQuantityRows() {
  showAllQuantityRows.value = !showAllQuantityRows.value
}

function add(a: number, b: number) {
  return new Decimal(a).plus(new Decimal(b)).toNumber()
}

function multiply(a: number, b: number) {
  return new Decimal(a).times(new Decimal(b)).toNumber()
}

const buyTotalPrice = computed(() => {
  return buyList.value.reduce((total, item) => {
    const price = multiply(getAlibabaCartUnitPrice(item, rangePrice.value), item.buyQuantity)
    return add(total, price)
  }, 0)
})

const buyTotalQuantity = computed(() => {
  return buyList.value.reduce((total, item) => total + item.buyQuantity, 0)
})

const orderRemark = ref('')

/**
 * 提交订单
 */
async function handleSubmit() {
  if (!mallUserInfo.value.id) {
    ElMessage.error($t('placeholder.pleaseLogined'))
    router.push(legacyPathToUserCenterPath('/login'))
    return
  }
  if (buyTotalQuantity.value < productInfo.value.minOrderQuantity) {
    ElMessage.error($t('placeholder.minAmountToBuy', { text: productInfo.value.minOrderQuantity }))
    return
  }

  mallAnalyticsLogEvent('alibaba_buy_now', buildAlibabaMallEventParams())
  // 弹出优惠价支付引导弹窗
  showDiscountModal.value = true
}

/** 优惠价支付弹窗确认后继续提交订单 */
function handleDiscountModalConfirm() {
  showDiscountModal.value = false
  submitOrder()
}

async function submitOrder() {

  const cargoParamList: any[] = []
  const skuTextList: string[] = []

  const offerId = productInfo.value.offerId
  buyList.value.forEach((item) => {
    cargoParamList.push({
      offerId,
      specId: item.specId,
      quantity: item.buyQuantity,
    })

    skuTextList.push(
      `${item.skuAttributes.map((item: any) => item.value).join(' ')} x${item.buyQuantity
      },`,
    )
  })

  const addrInfo = warehouseList.value[0] || {}

  const goodInfo = [
    {
      title: productInfo.value.subject,
      pic: productImages.value[0],
      skuInfo: skuTextList.join(' '),
      warehouseName: addrInfo.warehouseName,
      orderRemark: orderRemark.value,
    },
  ]

  const tmBindInfo: any = {
    thirdPartyId: '',
  }

  const params = {
    isInvoice: 0, // 是否开发票
    goodInfo: JSON.stringify(goodInfo), // 商品信息

    flow: 'general', // 以下字段解释对应文档中解释
    message: '',
    isvBizType: 'cross',
    tradeType: null,
    shopPromotionId: null,
    anonymousBuyer: null,
    fenxiaoChannel: null,
    inventoryMode: null,
    outOrderId: null,
    pickupService: null,
    warehouseCode: null,
    preSelectPayChannel: null,
    smallProcurement: null,
    addressId: null, // 以下对应文档里面的addressParam下的字段
    logisticsFullName: `${get(addrInfo, 'concatInfos[0].contactName')}(${tmBindInfo.thirdPartyId
      })`,
    logisticsMobile: get(addrInfo, 'concatInfos[0].contactMobile'),
    logisticsPhone: get(addrInfo, 'concatInfos[0].contactMobile'),
    logisticsPostCode: get(addrInfo, 'concatInfos[0].postCode'),
    logisticsCityText:
      get(addrInfo, 'concatInfos[0].city') || get(addrInfo, 'city'),
    logisticsProvinceText:
      get(addrInfo, 'concatInfos[0].province') || get(addrInfo, 'province'),
    logisticsAreaText:
      get(addrInfo, 'concatInfos[0].district') || get(addrInfo, 'district'),
    logisticsTownText: get(addrInfo, 'townName'),
    logisticsAddress: `${get(addrInfo, 'concatInfos[0].address')
      || get(addrInfo, 'address')}-(${tmBindInfo.thirdPartyId})`,
    fastCargoList: cargoParamList,

    invoiceType: null, // 以下对应invoiceParam下的字段
    provinceText:
      get(addrInfo, 'concatInfos[0].province') || get(addrInfo, 'province'),
    cityText: get(addrInfo, 'concatInfos[0].city') || get(addrInfo, 'city'),
    areaText:
      get(addrInfo, 'concatInfos[0].district') || get(addrInfo, 'district'),
    townText: get(addrInfo, 'townName'),
    postCode: get(addrInfo, 'concatInfos[0].postCode'),
    address: `${get(addrInfo, 'concatInfos[0].address')
      || get(addrInfo, 'address')}-(${tmBindInfo.thirdPartyId})`,
    fullName: `${get(addrInfo, 'concatInfos[0].contactName')}(${tmBindInfo.thirdPartyId
      })`,
    phone: get(addrInfo, 'concatInfos[0].contactMobile'),
    mobile: get(addrInfo, 'concatInfos[0].contactMobile'),
    companyName: null,
    taxpayerIdentifier: null,
    bankAndAccount: null,
    localInvoiceId: null,
  }

  const confirmation1688OrderData: any = []
  buyList.value.forEach((o, i) => {
    confirmation1688OrderData.push({
      id: i,
      itemId: offerId,
      itemSkuId: o.specId || o.skuId || offerId,  // 兼容多种字段名
      offerId: offerId,         // 添加 offerId 字段以匹配 APP 端格式
      // specId: o.specId || o.skuId || offerId,     // 兼容多种字段名，与APP端保持一致
      specId: o.specId || '',     // 兼容多种字段名，与APP端保持一致
      title: productInfo.value.subject,
      image: get(o, 'skuAttributes[0].skuImageUrl') || productImages.value[0],
      specsInfo: `${o.skuAttributes
        .map((item: any) => item.value)
        .join(' ')} x${o.buyQuantity}`,
      shopId: productInfo.value.sellerOpenId,
      shopNick: productInfo.value.sellerLoginId,
      quantity: o.buyQuantity,
      origin: 2,
      price: multiply(getAlibabaCartUnitPrice(o, rangePrice.value), 100),
      specsInfoText: `${o.skuAttributes
        .map((item: any) => item.value)
        .join(' ')} x${o.buyQuantity}`,
      categoryName: checkSendGoodType.value || '',
    })
  })
  console.log(productInfo.value,'confirmation1688OrderData',confirmation1688OrderData)
  window.localStorage.setItem(
    'Confirmation1688OrderData',
    JSON.stringify(confirmation1688OrderData),
  )

  router.push({
    path: legacyPathToUserCenterPath('/mall/confirmation1688Order'),
    query: {
      countryCode: checkSendCountryCode.value,
    },
  })
}

function makeOpenImgToSearch(url) {
  if (!url)
    return ''
  return `/product/search?searchType=img&imgSrc=${encodeURIComponent(url)}`
}

/**
 * 加入购物车
 */
async function handleAddToCart() {
  if (!mallUserInfo.value.id) {
    ElMessage.error($t('placeholder.pleaseLogined'))
    router.push(legacyPathToUserCenterPath('/login'))
    return
  }
  if (buyTotalQuantity.value < productInfo.value.minOrderQuantity) {
    ElMessage.error($t('placeholder.minAmountToBuy', { text: productInfo.value.minOrderQuantity }))
    return
  }

  const itemId = productInfo.value.offerId
  const param: any[] = []

  const shopNick = productInfo.value.sellerLoginId
  const shopId = productInfo.value.sellerOpenId
  const title = productInfo.value.subject
  buyList.value.forEach((item) => {
    let image = ''
    const specsInfo = item.skuAttributes
      .map((item: any) => {
        if (!image && item.skuImageUrl) {
          image = item.skuImageUrl
        }
        return item.value
      })
      .join(' ')

    if (!image) {
      image = productImages.value[0]
    }

    param.push({
      origin: 2,
      price: multiply(getAlibabaCartUnitPrice(item, rangePrice.value), 100),
      itemSkuId: item.specId || item.skuId || itemId,
      quantity: item.buyQuantity,
      image,
      itemId,
      title,
      specsInfo,
      shopNick,
      shopId,
      categoryName: checkSendGoodType.value || '',
    })
  })

  await tbGlobalAddItemsToCart({
    param,
  })

  mallAnalyticsLogEvent('alibaba_add_to_cart', buildAlibabaMallEventParams({
    quantity: buyTotalQuantity.value,
  }))
  showCartSuccessModal.value = true

  generateSkuMap()
}

function closeCartSuccessModal() {
  showCartSuccessModal.value = false
}

function goToCart() {
  showCartSuccessModal.value = false
  router.push(legacyPathToUserCenterPath('/mall/cart'))
}

/** 与 App 收藏接口一致：platformType 1688，itemPrice 为分（与购物车加价逻辑一致） */
const collectItemId1688 = computed(() => {
  const id = productInfo.value.offerId
  if (id != null && id !== '')
    return id
  return getRouteId() || ''
})

const collectItemTitle1688 = computed(() => {
  const useTrans = localeTo1688CountryMap[locale.value]
  const t = useTrans
    ? productInfo.value.subjectTrans
    : productInfo.value.subject
  return String(t || '').trim()
})

const collectItemPic1688 = computed(() => {
  const pic = productImages.value[0]
    || lodashGet(productInfo.value, 'productImage.images[0]')
  return String(pic || '').trim()
})

const collectItemPrice1688 = computed(() => {
  const raw = getAlibabaDefaultPriceFromSaleInfo(productInfo.value.productSaleInfo) || rangePrice.value
  const yuan = Number(raw)
  if (!Number.isFinite(yuan))
    return undefined
  return Math.round(new Decimal(yuan).times(100).toNumber())
})

const collectItemShopName1688 = computed(() => {
  return getTranslatedDetailText(productInfo.value.sellerLoginId)
})

const collectItemShopId1688 = computed(() =>
  String(productInfo.value.sellerOpenId || '').trim(),
)

const browseReportData = computed(() => {
  const data = productInfo.value || {}
  const useTranslatedTitle = Boolean(localeTo1688CountryMap?.[locale.value])
  const productSaleInfo = get(data, 'productSaleInfo') || {}
  const priceRangeList = Array.isArray(productSaleInfo?.priceRangeList) ? productSaleInfo.priceRangeList : []
  const fallbackRangePrice = (() => {
    if (!priceRangeList.length) return undefined
    const sortedPriceRangeList = [...priceRangeList].sort(
      (left, right) => Number(left?.startQuantity || 0) - Number(right?.startQuantity || 0),
    )
    const firstValidPrice = sortedPriceRangeList.find(
      (item) => toValidNumber(item?.promotionPrice) !== undefined || toValidNumber(item?.price) !== undefined,
    )
    return firstValidPrice ? getAlibabaEffectiveRangePrice(firstValidPrice) : undefined
  })()

  return {
    itemId: String(get(data, 'offerId') || detailId.value || getRouteId() || '').trim(),
    itemTitle: String(
      (useTranslatedTitle ? get(data, 'subjectTrans') : '')
      || get(data, 'subject')
      || get(data, 'name')
      || '',
    ).trim(),
    itemPic: String(
      get(data, 'productImage.images[0]')
      || get(data, 'productImage.imageUrl')
      || get(data, 'imageUrl')
      || get(data, 'image')
      || get(data, 'img')
      || get(data, 'images[0]')
      || '',
    ).trim(),
    categoryName: String(
      translatedCategoryInfo.value.categoryName
      || get(data, 'categoryNameTrans')
      || get(data, 'categoryName')
      || get(data, 'productCategoryName')
      || '',
    ).trim(),
    categoryId: get(data, 'categoryId')
      || get(data, 'productCategoryId')
      || get(data, 'leafCategoryId')
      || get(data, 'categoryNo')
      || translatedCategoryInfo.value.categoryId,
    itemShopId: String(
      get(data, 'sellerOpenId')
      || get(data, 'companyId')
      || get(data, 'companyNo')
      || get(data, 'sellerMemberId')
      || '',
    ).trim(),
    itemShopName: String(
      (useTranslatedTitle ? getTranslatedDetailText(get(data, 'sellerLoginId')) : '')
      || getTranslatedDetailText(get(data, 'sellerLoginId'))
      || get(data, 'sellerLoginId')
      || get(data, 'companyName')
      || get(data, 'shopName')
      || '',
    ).trim(),
    itemPrice: (() => {
      const raw =
        (getAlibabaDefaultPriceFromSaleInfo(productSaleInfo) || fallbackRangePrice)
        ?? get(data, 'priceInfo.price')
        ?? get(data, 'price')
        ?? get(data, 'salePrice')
        ?? get(data, 'skuPrice')
      const normalizedRaw = typeof raw === 'string' ? raw.replace(/,/g, '') : raw
      const price = Number(normalizedRaw)
      return Number.isFinite(price) ? Math.round(price * 100) : undefined
    })(),
  }
})

function reportBrowseBehaviorRecord() {
  if (!process.client) return
  if (!memberCode.value || !getMallAppToken()) return
  const currentItemId = browseReportData.value.itemId
  if (!currentItemId) return

  const reportKey = `${memberCode.value}:${currentItemId}`
  if (reportedBehaviorItemIds.has(reportKey)) return
  reportedBehaviorItemIds.add(reportKey)
  console.log(productInfo.value,"browseReportData.value", browseReportData.value);
  behaviorRecord({
    memberCode: memberCode.value,
    platformType: '1688',
    itemId: currentItemId,
    itemTitle: browseReportData.value.itemTitle,
    itemPic: browseReportData.value.itemPic,
    categoryName: browseReportData.value.categoryName,
    categoryId: browseReportData.value.categoryId,
    itemShopId: browseReportData.value.itemShopId,
    itemShopName: browseReportData.value.itemShopName,
    itemPrice: browseReportData.value.itemPrice,
    behaviorType: 1,
    browseDuration: 15,
    deviceType: 'web',
  }).catch(() => {
    reportedBehaviorItemIds.delete(reportKey)
  })
}

const { hasCollect, addToCollect, cancelCollect } = useCollect(collectItemId1688, {
  platformType: '1688',
  collectType: 1,
  itemTitle: collectItemTitle1688,
  itemPic: collectItemPic1688,
  itemPrice: collectItemPrice1688,
  itemShopName: collectItemShopName1688,
  itemShopId: collectItemShopId1688,
})

async function handleCollection() {
  if (!mallUserInfo.value.id) {
    ElMessage.error($t('placeholder.pleaseLogined'))
    router.push(legacyPathToUserCenterPath('/login'))
    return
  }
  if (hasCollect.value)
    await cancelCollect()
  else
    await addToCollect()
}

const currentAnchor = ref('description')

/**
 * 处理 productInfo.description 中的图片，为其添加 referrerpolicy="no-referrer"
 */
const processedDescription = computed(() => {
  if (!productInfo.value.description) return ''

  // 使用正则表达式找到所有 img 标签并添加 referrerpolicy="no-referrer"
  return productInfo.value.description.replace(
    /<img([^>]*?)>/gi,
    (match: string, attributes: string) => {
      // 检查是否已经有 referrerpolicy 属性
      if (attributes.includes('referrerpolicy=')) {
        // 如果已有 referrerpolicy 属性，替换它
        return `<img${attributes.replace(/referrerpolicy=["'][^"']*["']/gi, 'referrerpolicy="no-referrer"')}>`
      } else {
        // 如果没有 referrerpolicy 属性，添加它
        return `<img${attributes} referrerpolicy="no-referrer">`
      }
    }
  )
})

function handleToShopDetail() {
  const shopName = getTranslatedDetailText(productInfo.value.sellerLoginId)
  const shopId = productInfo.value.sellerOpenId

  router.push({
    name: 'shop',
    query: {
      shopName,
      shopId,
    },
  })
}

function copyLink() {
  copyText(window.location.href, $t('tbDetail.success.linkCopied'))
}

function shareToLine() {
  const url = encodeURIComponent(window.location.href)
  window.open(`https://social-plugins.line.me/lineit/share?url=${url}`, '_blank')
}

function shareToWechat() {
  copyText(window.location.href, $t('tbDetail.success.linkCopied'))
}

onMounted(async () => {
  cachedSelectedCountry.value = readGoodsDetailSelectedCountryCache()

  if (!page.value || !page.value.title) {
    getProductDetail()
  }
})
</script>

<template>
  <div class="goods-detail-page">
    <div class="goods-detail-page__back">
      <RouteBackButton />
    </div>
    <div v-if="productError" class="goods-detail-page__error">
      <div class="goods-detail-page__error-icon i-ep:warning" />
      <div>
        <h2 class="goods-detail-page__error-title">
          {{ $t('placeholder.sorryThisGoodsIsSoldOut') }}
        </h2>
        <div class="goods-detail-page__error-desc">
          {{ $t('placeholder.ifYouSureGoodsIsOnSalePleaseRefresh') }}
        </div>
      </div>
    </div>

    <div v-else class="goods-detail-page__container">
      <div class="goods-breadcrumbs">
        <span>{{ tAlibabaDetail('home', '首页') }}</span>
        <span class="goods-breadcrumbs__divider">/</span>
        <span>1688</span>
        <span class="goods-breadcrumbs__divider">/</span>
        <span class="is-current">{{ productSubjectText || tAlibabaDetail('productDetail', '商品详情') }}</span>
      </div>

      <section class="goods-hero">
        <div class="goods-hero__gallery">
          <div class="goods-media-card">
            <VueImageZoom v-if="productImg" :options="vueImageZoomOptions" />
            <div v-else class="goods-media-card__empty">
              {{ tAlibabaDetail('noImage', '暂无图片') }}
            </div>
          </div>

          <div class="goods-thumbs" v-if="heroThumbImages.length">
            <button v-for="(item, index) in heroThumbImages" :key="item" type="button" class="goods-thumb"
              :class="{ 'is-active': slideIndex === index }" @mouseenter="slideIndex = index"
              @click="slideIndex = index">
              <img referrerpolicy="no-referrer" :src="item" :alt="productSubjectText">
            </button>
          </div>

          <div class="goods-gallery-actions">
            <a class="ghost-pill-button" :target="isTarget_Blank()"
              :href="makeOpenImgToSearch(lodashGet(productInfo, 'productImage.images[0]'))">
              <span class="i-ep:camera" />
              <span>{{ $t('actions.searchLikeGoods') }}</span>
            </a>
            <div class="goods-gallery-actions__right">
              <div class="goods-share-wrap">
                <button type="button" class="text-link-button" @click="handleToggleSharePopup">
                  <span class="i-ep:share" />
                  <span>{{ $t('tbDetail.share') }}</span>
                </button>
                <div v-if="showSharePopup" class="goods-share-popup">
                  <div class="goods-share-popup__item goods-share-popup__item--active" @click="copyLink">
                    <span class="goods-share-popup__icon">
                      <span class="goods-share-popup__icon-bg" />
                      <span class="i-ep:link goods-share-popup__icon-inner" />
                    </span>
                    <span>{{ tAlibabaDetail('copyLink', '复制链接') }}</span>
                  </div>
                  <div class="goods-share-popup__item" @click="shareToLine">
                    <span class="goods-share-popup__icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="10" fill="#06C755" />
                        <path d="M5.5 10C5.5 7.5 7.5 5.5 10 5.5C12.5 5.5 14.5 7.5 14.5 10C14.5 12.5 12.5 14.5 10 14.5H6.5L5.5 15.5V10Z" fill="white" />
                      </svg>
                    </span>
                    <span>Line</span>
                  </div>
                  <div class="goods-share-popup__item" @click="shareToWechat">
                    <span class="goods-share-popup__icon">
                      <span class="goods-share-popup__icon-bg goods-share-popup__icon-bg--wechat" />
                      <span class="i-ep:chat-dot-round goods-share-popup__icon-inner" />
                    </span>
                    <span>{{ tAlibabaDetail('wechat', '微信') }}</span>
                  </div>
                </div>
              </div>
              <button type="button" class="text-link-button" @click="handleCollection">
                <span :class="[hasCollect ? 'i-ant-design:star-filled' : 'i-ant-design:star-outlined']"
                  class="goods-collect-icon" />
                <span>{{ tAlibabaDetail('collectText', '收藏') }}</span>
              </button>
            </div>

          </div>
        </div>

        <div class="goods-hero__panel">
          <div class="goods-hero__header">
            <div class="goods-badges">
              <span class="goods-badge goods-badge--source">
                <img class="goods-badge__icon" src="~/assets/icon1688.png" alt="1688" />
                <span>{{ tAlibabaDetail('badge1688Source', '1688來源') }}</span>
              </span>
              <span class="goods-badge goods-badge--dark">{{ tAlibabaDetail('badgeSelected', '精选') }}</span>
              <span class="goods-badge goods-badge--dark">{{ tAlibabaDetail('badgeDirectPurchase', '直接采购') }}</span>
            </div>

            <div class="goods-hero__title-row">
              <span class="goods-hero__poko-tag">
                <span class="goods-hero__poko-tag-label">1688</span>
                <span class="goods-hero__poko-tag-suffix">{{ tAlibabaDetail('proxyBuy', '代购') }}</span>
              </span>
              <h1 class="goods-hero__title">
                {{ productSubjectText }}
              </h1>

            </div>

            <div class="goods-info-row">
              <span class="goods-info-row__item">
                <span class="goods-info-row__label">{{ tAlibabaDetail('origin', '产地') }}</span>
                <span class="goods-info-row__value">{{ shippingOriginText }}</span>
              </span>
              <button type="button" class="goods-info-row__link" @click="handleToShopDetail">
                <span class="i-ep:shop" />
                <span>{{ tAlibabaDetail('directToShop', '直达店铺') }}</span>
              </button>
              <a
                class="goods-info-row__link"
                :href="originalProductUrl"
                :target="isTarget_Blank()"
                rel="noopener noreferrer"
              >
                <span class="i-ep:link" />
                <span>{{ tAlibabaDetail('productLink', '产品连接') }}</span>
              </a>
              <span class="goods-info-row__item">
                <span class="goods-info-row__label">{{ tAlibabaDetail('sold', '已售') }}</span>
                <span class="goods-info-row__value is-sold">80+{{ productUnitText || tAlibabaDetail('pieceUnit', '件')
                }}</span>
              </span>
            </div>

          </div>

          <div class="goods-price-grid">
            <div v-for="(item, index) in heroPriceCards" :key="`${item.label}-${index}`" class="goods-price-card">
              <div class="goods-price-card__qty">{{ item.label }}</div>
              <div class="goods-price-card__price">
                <span class="goods-price-card__currency">{{ getCurrencySymbol('¥') }}</span>
                <span class="goods-price-card__amount">{{ formatDisplayPrice(item.price) }}</span>
                <div class="goods-price-card__unit">/ {{ productUnitText || tAlibabaDetail('pieceUnit', '件') }}</div>
              </div>

            </div>
          </div>

          <div class="goods-logistics-flow">
            <div class="goods-logistics-flow__progress">
              <span class="goods-logistics-flow__pill">{{ tAlibabaDetail('chinaSegment', '中國段') }}</span>
              <div class="goods-logistics-flow__progress-line">
                <svg class="goods-logistics-flow__progress-icon" width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.9 0C12.3509 0 12.7833 0.179107 13.1021 0.49792C13.4209 0.816732 13.6 1.24914 13.6 1.7V9.18257C12.5496 8.57574 11.3172 8.36324 10.1242 8.58321C8.93113 8.80319 7.85559 9.44123 7.09062 10.3828C6.32566 11.3244 5.92138 12.5078 5.95038 13.7206C5.97937 14.9334 6.43974 16.0961 7.24882 17H1.7C1.24914 17 0.816732 16.8209 0.49792 16.5021C0.179107 16.1833 0 15.7509 0 15.3V1.7C0 1.24914 0.179107 0.816732 0.49792 0.49792C0.816732 0.179107 1.24914 0 1.7 0H11.9ZM11.05 10.2C11.9518 10.2 12.8166 10.5582 13.4542 11.1959C14.0918 11.8335 14.45 12.6983 14.45 13.6C14.45 14.5018 14.0918 15.3666 13.4542 16.0042C12.8166 16.6418 11.9518 17 11.05 17C10.1483 17 9.28348 16.6418 8.64586 16.0042C8.00823 15.3666 7.65002 14.5018 7.65002 13.6C7.65002 12.6983 8.00823 11.8335 8.64586 11.1959C9.28348 10.5582 10.1483 10.2 11.05 10.2ZM11.05 11.475C10.8418 11.4751 10.6409 11.5515 10.4853 11.6898C10.3297 11.8282 10.2303 12.0188 10.206 12.2256L10.2 12.325V13.6C10.2001 13.8082 10.2765 14.0092 10.4148 14.1648C10.5532 14.3203 10.7438 14.4197 10.9506 14.4441L11.05 14.45H11.9C12.1167 14.4498 12.3251 14.3668 12.4826 14.2181C12.6401 14.0694 12.7349 13.8661 12.7476 13.6498C12.7603 13.4336 12.69 13.2206 12.5509 13.0545C12.4119 12.8883 12.2146 12.7816 11.9995 12.756L11.9 12.75V12.325C11.9 12.0996 11.8105 11.8834 11.6511 11.724C11.4917 11.5646 11.2755 11.475 11.05 11.475ZM4.25001 7.65002H3.40001C3.17457 7.65002 2.95837 7.73957 2.79897 7.89898C2.63956 8.05839 2.55001 8.27459 2.55001 8.50002C2.55001 8.72546 2.63956 8.94166 2.79897 9.10106C2.95837 9.26047 3.17457 9.35002 3.40001 9.35002H4.25001C4.47545 9.35002 4.69165 9.26047 4.85105 9.10106C5.01046 8.94166 5.10001 8.72546 5.10001 8.50002C5.10001 8.27459 5.01046 8.05839 4.85105 7.89898C4.69165 7.73957 4.47545 7.65002 4.25001 7.65002ZM8.50002 4.25001H3.40001C3.18336 4.25025 2.97498 4.33321 2.81744 4.48193C2.65991 4.63066 2.56511 4.83392 2.55241 5.0502C2.53971 5.26647 2.61008 5.47944 2.74913 5.64557C2.88818 5.81171 3.08543 5.91848 3.30056 5.94406L3.40001 5.95001H8.50002C8.71667 5.94977 8.92505 5.86682 9.08259 5.71809C9.24012 5.56937 9.33492 5.3661 9.34762 5.14983C9.36032 4.93355 9.28995 4.72059 9.1509 4.55445C9.01185 4.38832 8.8146 4.28155 8.59947 4.25596L8.50002 4.25001Z" fill="#FF7228" />
                </svg>
              </div>
              <span class="goods-logistics-flow__pill">{{ tAlibabaDetail('crossBorderSegment', '跨境段') }}</span>
            </div>

            <div class="goods-logistics-flow__route">
              <div class="goods-logistics-flow__route-left">
                <svg class="goods-logistics-flow__route-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#route_clip)">
                    <path d="M3.99935 13C4.73572 13 5.33268 12.4031 5.33268 11.6667C5.33268 10.9303 4.73572 10.3334 3.99935 10.3334C3.26297 10.3334 2.66602 10.9303 2.66602 11.6667C2.66602 12.4031 3.26297 13 3.99935 13Z" stroke="#756865" stroke-linejoin="round" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.333 3.06738C10.6641 3.06773 10.9326 3.33584 10.9326 3.66699V5.40039H13.1895L13.2588 5.4043C13.4202 5.42315 13.5687 5.50658 13.668 5.6377L15.8105 8.47168C15.8892 8.57577 15.9316 8.70254 15.9316 8.83301V11.667C15.9315 11.9982 15.6633 12.2666 15.332 12.2666H13.5029C13.2502 13.0404 12.5231 13.5996 11.665 13.5996C10.8073 13.5993 10.0816 13.0402 9.8291 12.2666H5.33301C5.00175 12.2666 4.7326 11.9982 4.73242 11.667C4.73242 11.3356 5.00164 11.0674 5.33301 11.0674H9.73242V4.2666H1.26562V11.0674H2.66602C2.99724 11.0676 3.26562 11.3357 3.26562 11.667C3.26545 11.9981 2.99713 12.2664 2.66602 12.2666H0.666016C0.334753 12.2666 0.0665819 11.9982 0.0664062 11.667V3.66699C0.0664062 3.33562 0.334645 3.06738 0.666016 3.06738H10.333ZM11.665 10.9326C11.2605 10.933 10.9328 11.2624 10.9326 11.667C10.933 12.0715 11.2606 12.3991 11.665 12.3994C12.07 12.3994 12.3984 12.071 12.3984 11.666C12.3981 11.2613 12.0698 10.9326 11.665 10.9326ZM10.9326 9.87793C11.1585 9.78528 11.4057 9.73348 11.665 9.7334C12.5231 9.7334 13.2502 10.2928 13.5029 11.0664H14.7324V9.03516L12.8906 6.59961H10.9326V9.87793Z" fill="#756865" />
                  </g>
                  <defs>
                    <clipPath id="route_clip">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span class="goods-logistics-flow__route-origin">{{ logisticsOriginText }}</span>
                <span class="goods-logistics-flow__route-deliver">{{ tAlibabaDetail('deliverTo', '送至') }}</span>
                <span class="goods-logistics-flow__route-warehouse">{{ tAlibabaDetail('transferWarehouse', '中國大陸中轉倉')
                }}</span>
              </div>
              <div class="goods-logistics-flow__route-connector">
                <span class="goods-logistics-flow__inspection-label">{{ tAlibabaDetail('warehouseInspection', '入庫&質檢')
                  }}</span>
                <span class="goods-logistics-flow__route-line" />
              </div>
              <div class="goods-logistics-flow__route-right">
                <span class="goods-logistics-flow__route-warehouse">{{ tAlibabaDetail('transferWarehouse', '中國大陸中轉倉')
                }}</span>
                <span class="goods-logistics-flow__route-deliver">{{ tAlibabaDetail('deliverTo', '送至') }}</span>
                <el-select v-model="selectedAddress" class="goods-logistics-flow__address-select"
                  @change="handleSelectedAddressChange"
                  popper-class="goods-logistics-flow__address-popper" :placeholder="tAlibabaDetail('yourAddress', '您的地址')">
                  <el-option v-for="item in addressOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>
            </div>

            <div class="goods-logistics-flow__divider" />

            <div class="goods-logistics-flow__freight">
              <div class="goods-logistics-flow__freight-left">
                <span class="goods-logistics-flow__freight-label">{{ tAlibabaDetail('domesticFreight', '國內運費') }}</span>
                <span class="goods-logistics-flow__freight-tag">{{ domesticFreightText }}</span>
                <span class="goods-logistics-flow__freight-value">{{ internationalFreightEstimateText }}</span>
              </div>
              <div class="goods-logistics-flow__freight-right">
                <span class="goods-logistics-flow__freight-label">{{ tAlibabaDetail('internationalFreight', '國際運費')
                }}</span>
                <span class="goods-logistics-flow__freight-icon-wrap" @mouseenter="showFreightTooltip = true" @mouseleave="showFreightTooltip = false" @click="openFreightEstimate">
                  <svg class="goods-logistics-flow__freight-icon goods-logistics-flow__freight-icon--calc" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#calc_clip)">
                    <path d="M19 3C19.5046 2.99984 19.9906 3.19041 20.3605 3.5335C20.7305 3.87659 20.9572 4.34684 20.995 4.85L21 5V19C21.0002 19.5046 20.8096 19.9906 20.4665 20.3605C20.1234 20.7305 19.6532 20.9572 19.15 20.995L19 21H5C4.49542 21.0002 4.00943 20.8096 3.63945 20.4665C3.26947 20.1234 3.04284 19.6532 3.005 19.15L3 19V5C2.99984 4.49542 3.19041 4.00943 3.5335 3.63945C3.87659 3.26947 4.34684 3.04284 4.85 3.005L5 3H19ZM19 5H5V19H19V5ZM7.793 13.379L8.5 14.086L9.207 13.379C9.3956 13.1968 9.6482 13.096 9.9104 13.0983C10.1726 13.1006 10.4234 13.2058 10.6088 13.3912C10.7942 13.5766 10.8994 13.8274 10.9017 14.0896C10.904 14.3518 10.8032 14.6044 10.621 14.793L9.914 15.5L10.621 16.207C10.8032 16.3956 10.904 16.6482 10.9017 16.9104C10.8994 17.1726 10.7942 17.4234 10.6088 17.6088C10.4234 17.7942 10.1726 17.8994 9.9104 17.9017C9.6482 17.904 9.3956 17.8032 9.207 17.621L8.5 16.914L7.793 17.621C7.6044 17.8032 7.3518 17.904 7.0896 17.9017C6.8274 17.8994 6.57659 17.7942 6.39118 17.6088C6.20577 17.4234 6.1006 17.1726 6.09833 16.9104C6.09605 16.6482 6.19684 16.3956 6.379 16.207L7.086 15.5L6.379 14.793C6.28349 14.7008 6.20731 14.5904 6.1549 14.4684C6.10249 14.3464 6.0749 14.2152 6.07375 14.0824C6.0726 13.9496 6.0979 13.8179 6.14818 13.695C6.19846 13.5722 6.27271 13.4605 6.3666 13.3666C6.4605 13.2727 6.57215 13.1985 6.69505 13.1482C6.81794 13.0979 6.94962 13.0726 7.0824 13.0737C7.21518 13.0749 7.3464 13.1025 7.4684 13.1549C7.59041 13.2073 7.70075 13.2835 7.793 13.379ZM17 15.75C17.2549 15.7503 17.5 15.8479 17.6854 16.0228C17.8707 16.1978 17.9822 16.437 17.9972 16.6914C18.0121 16.9458 17.9293 17.1964 17.7657 17.3918C17.6021 17.5873 17.3701 17.7129 17.117 17.743L17 17.75H14C13.7451 17.7497 13.5 17.6521 13.3146 17.4772C13.1293 17.3022 13.0178 17.063 13.0028 16.8086C12.9879 16.5542 13.0707 16.3036 13.2343 16.1082C13.3979 15.9127 13.6299 15.7871 13.883 15.757L14 15.75H17ZM17 13.25C17.2652 13.25 17.5196 13.3554 17.7071 13.5429C17.8946 13.7304 18 13.9848 18 14.25C18 14.5152 17.8946 14.7696 17.7071 14.9571C17.5196 15.1446 17.2652 15.25 17 15.25H14C13.7348 15.25 13.4804 15.1446 13.2929 14.9571C13.1054 14.7696 13 14.5152 13 14.25C13 13.9848 13.1054 13.7304 13.2929 13.5429C13.4804 13.3554 13.7348 13.25 14 13.25H17ZM15.5 6.5C15.7652 6.5 16.0196 6.60536 16.2071 6.79289C16.3946 6.98043 16.5 7.23478 16.5 7.5V8H17C17.2652 8 17.5196 8.10536 17.7071 8.29289C17.8946 8.48043 18 8.73478 18 9C18 9.26522 17.8946 9.51957 17.7071 9.70711C17.5196 9.89464 17.2652 10 17 10H16.5V10.5C16.5 10.7652 16.3946 11.0196 16.2071 11.2071C16.0196 11.3946 15.7652 11.5 15.5 11.5C15.2348 11.5 14.9804 11.3946 14.7929 11.2071C14.6054 11.0196 14.5 10.7652 14.5 10.5V10H14C13.7348 10 13.4804 9.89464 13.2929 9.70711C13.1054 9.51957 13 9.26522 13 9C13 8.73478 13.1054 8.48043 13.2929 8.29289C13.4804 8.10536 13.7348 8 14 8H14.5V7.5C14.5 7.23478 14.6054 6.98043 14.7929 6.79289C14.9804 6.60536 15.2348 6.5 15.5 6.5ZM10 8C10.2652 8 10.5196 8.10536 10.7071 8.29289C10.8946 8.48043 11 8.73478 11 9C11 9.26522 10.8946 9.51957 10.7071 9.70711C10.5196 9.89464 10.2652 10 10 10H7C6.73478 10 6.48043 9.89464 6.29289 9.70711C6.10536 9.51957 6 9.26522 6 9C6 8.73478 6.10536 8.48043 6.29289 8.29289C6.48043 8.10536 6.73478 8 7 8H10Z" fill="currentColor" />
                  </g>
                  <defs>
                    <clipPath id="calc_clip">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <div v-if="showFreightTooltip" class="goods-freight-tooltip">
                  <div class="goods-freight-tooltip__arrow" />
                  <div class="goods-freight-tooltip__content">
                    <p class="goods-freight-tooltip__title">{{ tAlibabaDetail('freightTooltipTitle', '國際運費計算器') }}</p>
                    <p class="goods-freight-tooltip__desc">{{ tAlibabaDetail('freightTooltipDesc', '輸入尺寸重量，推薦線路及估算運費') }}</p>
                  </div>
                </div>
                </span>
                <span class="goods-logistics-flow__freight-icon-wrap" @mouseenter="showConsultTooltip = true" @mouseleave="showConsultTooltip = false" @click="goContactUs">
                  <svg class="goods-logistics-flow__freight-icon goods-logistics-flow__freight-icon--calc" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#svc_clip)">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5 9C5 7.14348 5.7375 5.36301 7.05025 4.05025C8.36301 2.7375 10.1435 2 12 2C13.8565 2 15.637 2.7375 16.9497 4.05025C18.2625 5.36301 19 7.14348 19 9V10.035C20.696 10.278 22 11.737 22 13.5V13.75C21.9999 14.1836 21.913 14.6128 21.7444 15.0123C21.5759 15.4119 21.3291 15.7736 21.0186 16.0763C20.7081 16.379 20.3401 16.6164 19.9365 16.7747C19.5328 16.933 19.1015 17.0089 18.668 16.998C17.928 19.118 16.046 20.547 14.015 20.909C13.545 21.081 12.989 21 12.5 21C12.1022 21 11.7206 20.842 11.4393 20.5607C11.158 20.2794 11 19.8978 11 19.5C11 19.1022 11.158 18.7206 11.4393 18.4393C11.7206 18.158 12.1022 18 12.5 18H13.5C13.7348 18 13.9664 18.055 14.176 18.1608C14.3856 18.2666 14.5675 18.4201 14.707 18.609C16.003 17.992 17 16.689 17 15V9C17 7.67392 16.4732 6.40215 15.5355 5.46447C14.5979 4.52678 13.3261 4 12 4C10.6739 4 9.40215 4.52678 8.46447 5.46447C7.52678 6.40215 7 7.67392 7 9V15.25C7 15.7141 6.81563 16.1592 6.48744 16.4874C6.15925 16.8156 5.71413 17 5.25 17C4.38805 17 3.5614 16.6576 2.9519 16.0481C2.34241 15.4386 2 14.612 2 13.75V13.5C1.99978 12.6582 2.30296 11.8446 2.85395 11.2082C3.40495 10.5718 4.16685 10.1553 5 10.035V9ZM5 12.085C4.70742 12.1884 4.45413 12.3801 4.27503 12.6335C4.09593 12.8869 3.99984 13.1897 4 13.5V13.75C4 14.355 4.43 14.86 5 14.975V12.085ZM19 12.085V14.975C19.57 14.859 20 14.355 20 13.75V13.5C20.0002 13.1897 19.9041 12.8869 19.725 12.6335C19.5459 12.3801 19.2926 12.1884 19 12.085Z" fill="currentColor" />
                  </g>
                  <defs>
                    <clipPath id="svc_clip">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <div v-if="showConsultTooltip" class="goods-freight-tooltip">
                  <div class="goods-freight-tooltip__arrow" />
                  <div class="goods-freight-tooltip__content">
                    <p class="goods-freight-tooltip__title">{{ tAlibabaDetail('consultTooltipTitle', '購物諮詢') }}</p>
                    <p class="goods-freight-tooltip__desc">{{ tAlibabaDetail('consultTooltipDesc', '幫您詢問商品尺寸和重量，寄送專家精準推薦路線和運費') }}</p>
                  </div>
                </div>
                </span>

              </div>
            </div>
          </div>

          <CheckSendAvailabilityCard
            :country-code="checkSendCountryCode"
            :good-type="checkSendGoodType"
            :categories="checkSendDisplayCategories"
            :match-transport-goods="true"
            :transport-category-name="checkSendGoodType"
          />

          <div class="goods-panel-card">
            <div class="goods-service-commitment">
              <span class="i-ep:circle-check-filled goods-service-commitment__icon" />
              <span class="goods-service-commitment__label">{{ tAlibabaDetail('serviceCommitment', '服務承諾') }}</span>
              <span class="goods-service-commitment__link">{{ tAlibabaDetail('purchaseTimelyReminder', '採購實效提醒')
                }}</span>
              <span class="goods-service-commitment__link">{{ tAlibabaDetail('quickRefund', '極速退款') }}</span>
              <span class="goods-service-commitment__disclaimer-wrap">
                <span class="goods-service-commitment__link" @click="handleToggleDisclaimerPopup">{{
                  tAlibabaDetail('disclaimerText', '免責聲明') }}</span>
                <span class="i-ep:arrow-down goods-service-commitment__disclaimer-arrow" />
                <div v-if="showDisclaimerPopup" class="goods-disclaimer-popup">
                  <div class="goods-disclaimer-popup__arrow" />
                  <div class="goods-disclaimer-popup__content">
                    <div class="goods-disclaimer-popup__title">{{ tAlibabaDetail('disclaimerPopupTitle', '3項服務保障中') }}
                    </div>
                    <div class="goods-disclaimer-popup__list">
                      <div class="goods-disclaimer-popup__item">
                        <span class="goods-disclaimer-popup__item-icon i-ep:clock" />
                        <div class="goods-disclaimer-popup__item-text">
                          <span class="goods-disclaimer-popup__item-title">{{ tAlibabaDetail('purchaseTimelyReminder',
                            '采购实效提醒') }}</span>
                          <span class="goods-disclaimer-popup__item-desc">{{
                            tAlibabaDetail('purchaseTimelyReminderDesc',
                            '採購實效提醒服務為您提供採購進度的實時跟踪和提醒，確保商品能夠按時發貨。如遇到發貨延遲，我們將主動通知您，並協助處理相關問題，讓您的採購體驗更加安心無憂。') }}</span>
                        </div>
                      </div>
                      <div class="goods-disclaimer-popup__item">
                        <span class="goods-disclaimer-popup__item-icon i-ep:wallet" />
                        <div class="goods-disclaimer-popup__item-text">
                          <span class="goods-disclaimer-popup__item-title">{{ tAlibabaDetail('quickRefund', '極速退款')
                            }}</span>
                          <span class="goods-disclaimer-popup__item-desc">{{ tAlibabaDetail('quickRefundDesc',
                            '滿足相應條件(剪標/使用/洗滌後不支持)時，消費者可申請"7天無理由退貨"') }}</span>
                        </div>
                      </div>
                      <div class="goods-disclaimer-popup__item">
                        <span class="goods-disclaimer-popup__item-icon i-ep:warning-filled" />
                        <div class="goods-disclaimer-popup__item-text">
                          <span class="goods-disclaimer-popup__item-title">{{ tAlibabaDetail('disclaimerText', '免責聲明')
                            }}</span>
                          <span class="goods-disclaimer-popup__item-desc">{{ disclaimerPopupDescParts.part1 }}<strong>{{ disclaimerPopupDescParts.part2 }}</strong></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </span>
              <div class="goods-service-commitment__assistant" @click="handleToggleAssistantPopup">
                <span class="i-ep:info-filled" />
                <span>{{ tAlibabaDetail('shoppingAssistant', '購物助手') }}</span>

                <div v-if="showAssistantPopup" class="goods-assistant-popup">
                  <div class="goods-assistant-popup__arrow" />
                  <div class="goods-assistant-popup__item" @click.stop="goFreightEstimate">
                    <span>{{ tAlibabaDetail('freightEstimate', '运费估算') }}</span>
                    <span class="i-ep:arrow-right" />
                  </div>
                  <div class="goods-assistant-popup__item" @click.stop="goTransshipment">
                    <span>{{ tAlibabaDetail('transshipment', '转运集运') }}</span>
                    <span class="i-ep:arrow-right" />
                  </div>
                </div>
              </div>
            </div>

            <div v-for="(spec, index) in selectionSpecs" :key="spec.attributeId || index" class="goods-spec-group">
              <div class="goods-spec-group__label">
                {{ getSpecDisplayName(spec) }}
              </div>
              <div class="goods-spec-values">
                <button v-for="attrValue in spec.attributeValues" :key="attrValue.value" type="button"
                  class="goods-spec-value"
                  :class="{ 'is-selected': attrValueIsSelected(spec.attributeId, attrValue.value) }"
                  @click="handleSelectSpecValue(spec.attributeId, attrValue)">
                  <el-image v-if="attrValue.skuImageUrl" class="goods-spec-value__image" referrerpolicy="no-referrer"
                    :src="attrValue.skuImageUrl" :alt="getSpecDisplayValue(attrValue)"
                    :preview-src-list="skuPreviewImages" :initial-index="getSkuPreviewImageIndex(attrValue.skuImageUrl)"
                    preview-teleported fit="cover" @click.stop />
                  <span>{{ getSpecDisplayValue(attrValue) }}</span>
                </button>
              </div>
            </div>

            <div class="goods-spec-group" v-if="quantitySpec && quantityRows.length">
              <div class="goods-spec-group__label">
                {{ getSpecDisplayName(quantitySpec) }}
              </div>

              <div class="goods-quantity-table">
                <div class="goods-quantity-table__head">
                  <span>{{ tAlibabaDetail('specification', '規格') }}</span>
                  <span>{{ $t('texts.goodsItemPrice') }}</span>
                  <span>{{ tAlibabaDetail('inventory', '庫存') }}</span>
                  <span>{{ $t('texts.buyAmount') }}</span>
                </div>

                <div v-for="attrValue in previewQuantityRows" :key="attrValue.value" class="goods-quantity-row">
                  <div class="goods-quantity-row__spec">
                    <el-image v-if="attrValue.skuImageUrl" class="goods-quantity-row__image"
                      referrerpolicy="no-referrer" :src="attrValue.skuImageUrl" :alt="getSpecDisplayValue(attrValue)"
                      :preview-src-list="skuPreviewImages"
                      :initial-index="getSkuPreviewImageIndex(attrValue.skuImageUrl)" preview-teleported fit="cover" />
                    <div class="goods-quantity-row__content">
                      <div class="goods-quantity-row__title">
                        {{ getSpecDisplayValue(attrValue) }}
                      </div>
                      <div class="goods-quantity-row__sub">
                        {{ tAlibabaDetail('mixedBatchSupport', '支持多規格混批') }}
                      </div>
                    </div>
                  </div>

                  <div class="goods-quantity-row__price">
                    {{ formatDisplayMoney(getAlibabaCartUnitPrice(getSkuInfoByAttrValue(quantitySpec.attributeId, attrValue.value), rangePrice)) }}
                  </div>

                  <div class="goods-quantity-row__stock">
                    {{ getSkuInfoByAttrValue(quantitySpec.attributeId, attrValue.value)?.amountOnSale || 0 }}
                    {{ productUnitText }}
                  </div>

                  <div class="goods-stepper" v-if="getSkuInfoByAttrValue(quantitySpec.attributeId, attrValue.value)">
                    <button type="button" class="goods-stepper__button"
                      @click="updateSkuQuantity(getSkuInfoByAttrValue(quantitySpec.attributeId, attrValue.value).skuId, -1)">
                      -
                    </button>
                    <input class="goods-stepper__input"
                      :value="getSkuInfoByAttrValue(quantitySpec.attributeId, attrValue.value).buyQuantity"
                      @input="handleSkuQuantityInput(getSkuInfoByAttrValue(quantitySpec.attributeId, attrValue.value).skuId, $event)">
                    <button type="button" class="goods-stepper__button"
                      @click="updateSkuQuantity(getSkuInfoByAttrValue(quantitySpec.attributeId, attrValue.value).skuId, 1)">
                      +
                    </button>
                  </div>
                </div>

                <button v-if="hasMoreQuantityRows" type="button" class="goods-quantity-table__toggle"
                  @click="handleToggleQuantityRows">
                  {{ showAllQuantityRows ? tAlibabaDetail('collapseSpecs', '收起規格') : tAlibabaDetail('expandSpecs',
                    '展开更多规格') }}
                </button>
              </div>
            </div>

            <div class="goods-disclaimer">
              <p>{{ platformDisclaimerParts.part1 }}<strong>{{ platformDisclaimerParts.part2 }}</strong></p>
              <div class="goods-agreement">
                <el-checkbox v-model="agreementChecked">
                  {{ $t('tbDetail.agreementConsent') }}
                </el-checkbox>
              </div>
            </div>

            <!-- <div class="goods-payment">
              <span class="goods-payment__label">{{ tAlibabaDetail('paymentSupport', '支付支持') }}</span>
              <div class="goods-payment__icons">
                <svg class="goods-payment__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1.5" y="3" width="21" height="16" rx="3" stroke="#3F3B3A" stroke-width="1.5" />
                  <rect x="1.5" y="6" width="21" height="3" fill="#3F3B3A" />
                  <rect x="4" y="14" width="6" height="2" rx="0.5" fill="#3F3B3A" opacity="0.4" />
                </svg>
                <img class="goods-payment__icon" :src="paymentIcons.wechatPay" alt="WeChat Pay" />
                <img class="goods-payment__icon" :src="paymentIcons.alipay" alt="Alipay" />
              </div>
            </div> -->

	            <div class="goods-price-summary">
	              <span class="goods-price-summary__left">
	                {{ tAlibabaDetail('selectedItems', '已选') }} <span class="orange-text">{{ buyList.length }}</span> {{
	                  tAlibabaDetail('itemUnit', '款') }} <span  class="orange-text">{{ buyTotalQuantity }}</span> {{ tAlibabaDetail('pieceUnit', '件') }}
	              </span>
              <div class="goods-price-summary__right">
                <div class="goods-price-summary__row">
                  <span class="goods-price-summary__row-label">{{ tAlibabaDetail('itemSubtotal', '商品金额') }}</span>
                  <span class="goods-price-summary__row-currency">{{ getCurrencySymbol('¥') }}</span>
                  <span class="goods-price-summary__row-amount">{{ formatDisplayPrice(summaryDisplayPrice) }}</span>
                  <span class="goods-price-summary__row-separator">/</span>
                  <span class="goods-price-summary__row-unit">{{ productUnitText || tAlibabaDetail('pieceUnit', '件')
                  }}</span>
                </div>
                <div class="goods-price-summary__row">
                  <span class="goods-price-summary__row-label">{{ tAlibabaDetail('extraFreight', '另需运费(预估)') }}</span>
                  <span class="goods-price-summary__row-currency">{{ getCurrencySymbol('¥') }}</span>
                  <span class="goods-price-summary__row-amount is-muted">{{ internationalFreightAmountText }}</span>
                  <span class="goods-price-summary__row-separator">/</span>
                  <span class="goods-price-summary__row-unit">{{ productUnitText || tAlibabaDetail('pieceUnit', '件')
                  }}</span>
                </div>
              </div>
            </div>

            <div class="goods-cta-row">
              <button type="button" class="theme-button theme-button--secondary" @click="handleAddToCart">
                <span class="i-ep:shopping-cart-full" />
                <span>{{ $t('actions.addBuyCart') }}</span>
              </button>
              <button type="button" class="theme-button theme-button--primary" @click="handleSubmit">
                <span class="i-ep:shopping-bag" />
                <span>{{ tAlibabaDetail('payAfterDiscount', '優惠後支付') }}</span>
              </button>
              <button type="button" class="theme-bookmark-button" @click="handleCollection">
                <span :class="[hasCollect ? 'i-ant-design:star-filled' : 'i-ant-design:star-outlined']"
                  style="font-size: 20px" />
                <span>{{ $t('actions.collect') }}({{ hasCollect ? '1' : '0' }})</span>
              </button>
            </div>
          </div>

          <div class="goods-store-section">
            <div class="goods-store-section__top">
              <span class="goods-store-tag">
                <span class="goods-store-tag__icon i-custom:alibaba-1688" />
                <span>{{ tAlibabaDetail('sourceFactory', '源頭工廠·1688實力供應商') }}</span>
              </span>
              <button type="button" class="goods-store-enter-btn" @click="handleToShopDetail">
                <span>{{ tAlibabaDetail('enterShop', '進入店鋪') }}</span>
                <span class="i-ep:arrow-right" />
              </button>
            </div>

            <div class="goods-store-info">
              <div class="goods-store-info__logo">
                <img v-if="productInfo.sellerLogo" referrerpolicy="no-referrer" :src="productInfo.sellerLogo"
                  :alt="sellerDisplayName">
                <span v-else class="i-custom:alibaba-1688" style="font-size: 28px; color: #ff7228" />
              </div>

              <div class="goods-store-info__details">
                <div class="goods-store-info__row">
                  <span class="goods-store-info__label">{{ tAlibabaDetail('supplierName', '供應商名稱') }}：</span>
                  <span class="goods-store-info__value is-name">{{ sellerDisplayName }}</span>
                </div>
                <div class="goods-store-info__row">
                  <span class="goods-store-info__label">{{ tAlibabaDetail('supplierType', '供應商類型') }}：</span>
                  <span class="goods-store-tag is-sm">
                    <span class="i-ep:medal" style="font-size: 12px" />
                    <span>{{ supplierTypeText }}</span>
                    <template v-if="supplierTradeLevel">
                      <span style="margin: 0 4px; color: #999;">|</span>
                      <span>v{{ supplierTradeLevel }}</span>
                    </template>
                  </span>
                </div>
                <div class="goods-store-info__row">
                  <span class="goods-store-info__label">{{ tAlibabaDetail('location', '所在地') }}：</span>
                  <span class="goods-store-info__value">{{ shippingOriginText }}</span>
                </div>
                <div class="goods-store-info__row">
                  <span class="goods-store-info__label">{{ tAlibabaDetail('rating', '綜合評分') }}：</span>
                  <span class="goods-store-stars">
                    <span v-for="i in 5" :key="i" class="i-ep:star-filled" :class="{ 'is-filled': i <= supplierStarCount }" />
                  </span>
                  <span class="goods-store-info__value is-rating">{{ supplierRatingText }}</span>
                </div>
              </div>

              <!-- <div class="goods-store-info__ratings">
                <div class="goods-store-info__rating-item">
                  <div class="goods-store-info__rating-header">
                    <span class="goods-store-info__rating-label">{{ $t('tbDetail.productQuality') }}</span>
                    <span class="goods-store-info__rating-score">{{ qualityRating.toFixed(1) }}</span>
                    <span class="goods-store-info__rating-level" :class="getRatingLevel(qualityRating).class">{{ getRatingLevel(qualityRating).text }}</span>
                  </div>
                </div>
                <div class="goods-store-info__rating-item">
                  <div class="goods-store-info__rating-header">
                    <span class="goods-store-info__rating-label">{{ $t('tbDetail.logisticsSpeed') }}</span>
                    <span class="goods-store-info__rating-score">{{ shippingRating.toFixed(1) }}</span>
                    <span class="goods-store-info__rating-level" :class="getRatingLevel(shippingRating).class">{{ getRatingLevel(shippingRating).text }}</span>
                  </div>
                </div>
                <div class="goods-store-info__rating-item">
                  <div class="goods-store-info__rating-header">
                    <span class="goods-store-info__rating-label">{{ $t('tbDetail.serviceGuarantee') }}</span>
                    <span class="goods-store-info__rating-score">{{ serviceRating.toFixed(1) }}</span>
                    <span class="goods-store-info__rating-level" :class="getRatingLevel(serviceRating).class">{{ getRatingLevel(serviceRating).text }}</span>
                  </div>
                </div>
              </div> -->

              <div class="goods-store-info__divider" />

              <div class="goods-store-info__metrics">
                <div class="goods-store-info__row">
                  <span class="goods-store-info__label">{{ tAlibabaDetail('transactions', '成交量') }}：</span>
                  <span class="goods-store-info__value is-metric">{{ supplierTransactionText }}</span>
                </div>
                <div class="goods-store-info__row">
                  <span class="goods-store-info__label">{{ tAlibabaDetail('repurchaseRate', '回購率') }}：</span>
                  <span class="goods-store-info__value is-metric">{{ supplierRepurchaseRate }}</span>
                </div>
                <div class="goods-store-info__row">
                  <span class="goods-store-info__label">{{ tAlibabaDetail('minBatchQty', '起批量') }}：</span>
                  <span class="goods-store-info__value">{{ productInfo.minOrderQuantity || 2 }}{{ productUnitText ||
                    tAlibabaDetail('pieceUnit', '件') }}{{ tAlibabaDetail('minOrderSuffix', '起') }}</span>
                </div>
                <div class="goods-store-info__row">
                  <span class="goods-store-info__label">{{ tAlibabaDetail('certification', '資格證') }}：</span>
                  <span class="goods-store-info__value is-metric">{{ supplierCertification }}</span>
                </div>
              </div>
            </div>

            <div class="goods-store-verification">
              <div class="goods-store-verification__title">
                {{ tAlibabaDetail('factoryVerification', '工廠實景·實力驗證') }}
              </div>
              <div class="goods-store-verification__images">
                <div
                  v-for="(img, index) in (supplierFactoryImages.length ? supplierFactoryImages : [])"
                  :key="'factory-' + index" class="goods-store-verification__image">
                  <img referrerpolicy="no-referrer" :src="img" alt="" />
                </div>
                <div v-if="!supplierFactoryImages.length"
                  class="goods-store-verification__empty">
                  {{ tAlibabaDetail('noFactoryImages', '暫無工廠實景圖片') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="goods-content-grid">
        <div class="goods-detail-card">
          <el-tabs v-model="currentAnchor" class="goods-detail-tabs">
            <el-tab-pane :label="$t('texts.imagesInfoDetails')" name="description">
              <div class="tab-goods-detail-panel">
                <div class="goods-parameter-section" v-if="parameterItems.length">
                  <h3 class="goods-parameter-section__title">{{ tAlibabaDetail('productAttributes', '商品屬性') }}</h3>
                  <div class="goods-parameter-grid">
                    <div v-for="item in parameterItems" :key="item.attributeName" class="goods-parameter-item">
                      <div class="goods-parameter-item__label">{{ getAttributeDisplayName(item) }}</div>
                      <div class="goods-parameter-item__value">{{ getAttributeDisplayValue(item) }}</div>
                    </div>
                  </div>
                </div>
                <h3 class="goods-parameter-section__title">{{ tAlibabaDetail('productImages', '商品圖片') }}</h3>
                <div class="goods-detail-intro">
                  <div class="goods-detail-intro__eyebrow">1688 PRODUCT DETAIL</div>
                  <h2>{{ productSubjectText }}</h2>
                  <p>{{ productSummaryText }}</p>
                </div>
                
                <div class="goods-visual-showcase" v-if="productImages.length">
                  <div class="goods-visual-showcase__main">
                    <img referrerpolicy="no-referrer" :src="productImages[0]" :alt="productSubjectText">
                  </div>
                  <div class="goods-visual-showcase__grid" v-if="editorialFeatureCards.length">
                    <div v-for="(card, index) in editorialFeatureCards" :key="`${card.title}-${index}`"
                      class="goods-visual-feature-card">
                      <div class="goods-visual-feature-card__image">
                        <img referrerpolicy="no-referrer" :src="card.image" :alt="card.title">
                      </div>
                      <div class="goods-visual-feature-card__body">
                        <h3>{{ card.title }}</h3>
                        <p>{{ card.desc }}</p>
                      </div>
                    </div>
                  </div>

                  <div class="goods-eco-card">
                    <div class="goods-eco-card__icon">
                      <span class="i-ep:opportunity" />
                    </div>
                    <div class="goods-eco-card__title">{{ editorialCallout.title }}</div>
                    <div class="goods-eco-card__desc">{{ editorialCallout.desc }}</div>
                  </div>
                </div>

                <div v-if="processedDescription" class="goods-native-description">
                  <div class="goods-native-description__head">
                    <div class="goods-native-description__eyebrow">1688 ORIGINAL CONTENT</div>
                    <h3>{{ tAlibabaDetail('originalContentTitle', '原始圖文詳情') }}</h3>
                  </div>
                  <div class="description-html" v-html="processedDescription" />
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane :label="$t('texts.proxyBuyGuide')" name="guide">
              <div class="goods-detail-panel goods-copy-panel">
                <h6>{{ $t('proxyBuyGuide.unAccessGoods') }}</h6>
                <p>{{ $t('proxyBuyGuide.unAccess1') }}.</p>
                <p class="is-muted">{{ $t('proxyBuyGuide.unAccessDetail1') }}.</p>
                <div class="goods-copy-panel__images">
                  <img src="https://cdn.static.hoobuy.com/assets/product/prohibited_1.webp" alt="">
                  <img src="https://cdn.static.hoobuy.com/assets/product/prohibited_2.webp" alt="">
                </div>

                <h6>{{ $t('proxyBuyGuide.unAccessGoods1') }}.</h6>
                <p>{{ $t('proxyBuyGuide.unAccessGoods1Detail') }}.</p>
                <div class="goods-copy-panel__images goods-copy-panel__images--wide">
                  <img src="https://cdn.static.hoobuy.com/assets/product/limited_1.webp" alt="">
                  <img src="https://cdn.static.hoobuy.com/assets/product/limited_2.webp" alt="">
                  <img src="https://cdn.static.hoobuy.com/assets/product/limited_3.webp" alt="">
                  <img src="https://cdn.static.hoobuy.com/assets/product/limited_4.webp" alt="">
                </div>

                <h6>{{ $t('texts.warningTip') }}</h6>
                <p>{{ $t('proxyBuyGuide.unAccessGoods1Detail2') }}.</p>
              </div>
            </el-tab-pane>

            <el-tab-pane :label="$t('texts.saledService')" name="service">
              <div class="goods-detail-panel goods-copy-panel">
                <div class="goods-service-copy">
                  <p>{{ $t('proxyBuyGuide.warningTip1') }}.</p>
                  <p>{{ $t('proxyBuyGuide.warningTip2') }}.</p>
                  <p>{{ $t('proxyBuyGuide.warningTip3') }}.</p>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </section>

      <!-- 代購流程說明 -->
      <PurchaseFlowGuide />

      <!-- 优惠价支付引导弹窗 -->
      <DiscountModal
        v-model="showDiscountModal"
        @confirm="handleDiscountModalConfirm"
      />

      <ProductRecommendationSection
        :fetch-page="fetch1688RecommendationPage"
        :format-price="format1688RecommendationPrice"
        :get-link="getRecommendLink"
        :reload-key="recommendReloadKey"
        :target="isTarget_Blank()"
        :title="tAlibabaDetail('hotRecommend', '熱門商品推薦')"
      />
    </div>

    <!-- 加入购物车成功弹窗 (Figma design) -->
    <div v-if="showCartSuccessModal" class="cart-success-modal-overlay" @click.self="closeCartSuccessModal">
      <div class="cart-success-modal">
        <div class="cart-success-modal__content">
          <span class="cart-success-modal__icon">
            <span class="i-ep:circle-check-filled" />
          </span>
          <div class="cart-success-modal__text-area">
            <p class="cart-success-modal__title">{{ $t('messages.addToBuyCardSuccess') }}</p>
            <p class="cart-success-modal__link" @click="goToCart">{{ $t('alibabaDetail.viewMyCart') }}&gt;</p>
          </div>
        </div>
        <button type="button" class="cart-success-modal__button" @click="closeCartSuccessModal">
          {{ $t('tbShop.confirm') }}
        </button>
        <div class="cart-success-modal__divider" />
      </div>
    </div>
  </div>
</template>

<style scoped>
* {
  box-sizing: border-box;
}

.goods-detail-page {
  --goods-primary: var(--el-color-primary, #456805);
  --goods-primary-soft: var(--el-color-primary-light-9, rgba(69, 104, 5, 0.08));
  --goods-primary-softer: var(--el-color-primary-light-8, rgba(69, 104, 5, 0.12));
  --goods-primary-dark: var(--el-color-primary-dark-1, #375204);
  --goods-text: #1b1c1c;
  --goods-text-muted: #44493a;
  --goods-text-light: #7d846d;
  --goods-border: rgba(196, 201, 181, 0.3);
  --goods-border-soft: rgba(196, 201, 181, 0.2);
  --goods-border-strong: rgba(196, 201, 181, 0.5);
  --goods-surface: #ffffff;
  --goods-surface-muted: #fbf9f8;
  --goods-surface-strong: #f5f3f3;
  --goods-surface-deep: #efeded;
  --goods-shadow: 0 4px 15px rgba(46, 77, 50, 0.03);
  color: var(--goods-text);
  background:#FBFAF9;
}

.goods-detail-page__container {
  width: min(1188px, calc(100vw - 48px));
  margin: 0 auto;
  padding: 24px 0 56px;
}

.goods-detail-page__back {
  width: min(1188px, calc(100vw - 48px));
  margin: 0 auto;
  padding-top: 24px;
}

.goods-detail-page__error {
  width: min(1188px, calc(100vw - 48px));
  margin: 40px auto;
  padding: 28px 32px;
  background: var(--goods-surface);
  border: 1px solid var(--goods-border-soft);
  border-radius: 12px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  box-shadow: var(--goods-shadow);
}

.goods-detail-page__error-icon {
  font-size: 40px;
  color: #d97706;
}

.goods-detail-page__error-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.goods-detail-page__error-desc {
  margin-top: 10px;
  color: var(--goods-text-muted);
  font-size: 14px;
}

.goods-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  color: var(--goods-text-light);
  font-size: 12px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

.goods-breadcrumbs__divider {
  opacity: 0.6;
}

.goods-breadcrumbs .is-current {
  color: #ff7228;
  font-weight: 600;
}

.goods-hero {
  display: grid;
  grid-template-columns: minmax(0, 452px) minmax(0, 1fr);
  gap: 32px;
  padding: 32px;
  background: var(--goods-surface);
  border: 1px solid var(--goods-border-soft);
  /* border-radius: 12px; */
  box-shadow: var(--goods-shadow);
}

.goods-hero__gallery,
.goods-hero__panel {
  min-width: 0;
}

.goods-hero__panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.goods-media-card {
  background: var(--goods-surface-deep);
  border: 1px solid var(--goods-border);
  border-radius: 8px;
  overflow: hidden;
  min-height: 452px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.goods-media-card__empty {
  color: var(--goods-text-light);
  font-size: 14px;
}

.goods-thumbs {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.goods-thumb {
  width: 80px;
  height: 80px;
  padding: 0;
  border-radius: 6px;
  border: 1px solid var(--goods-border);
  background: var(--goods-surface);
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.goods-thumb.is-active {
  border: 2px solid #ff7228;
}

.goods-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.goods-gallery-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid var(--goods-border-soft);
  flex-wrap: wrap;
  justify-content: space-between;
}

.ghost-pill-button,
.text-link-button,
.theme-button,
.goods-stepper__button,
.goods-spec-value,
.goods-thumb {
  appearance: none;
  outline: none;
  font: inherit;
}

.ghost-pill-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid var(--goods-border);
  background: var(--goods-surface);
  color: #ff7228;
  text-decoration: none;
  cursor: pointer;
  font-size: 12px;
  line-height: 16px;
}

.text-link-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #908380;
  cursor: pointer;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
}

.text-link-button:hover,
.ghost-pill-button:hover {
  color: #645a56;
}

.goods-collect-icon {
  font-size: 16px;
  color: #ff7228;
}

.goods-hero__header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.goods-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.goods-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #ffe8d4;
  color: #ff7228;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.goods-badge--dark {
  background: #9d240e;
  color: #fff;
}

.goods-badge--source {
  background: #ffe8d4;
  color: #fe5911;
}

.goods-badge__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.goods-hero__title-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.goods-hero__title {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  color: #333;
  flex: 1;
  min-width: 0;
}

.goods-hero__poko-tag {
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.goods-hero__poko-tag-label {
  padding: 1px 6px;
  background: #ff7228;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}

.goods-hero__poko-tag-suffix {
  padding: 1px 6px;
  background: #fff;
  color: #ff600a;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  border: 1px solid #ff7228;
  border-left: 0;
  border-radius: 0 4px 4px 0;
}

.goods-info-row {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.goods-info-row__item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.goods-info-row__label {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #645a56;
}

.goods-info-row__value {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #1a1817;
}

.goods-info-row__value.is-sold {
  font-weight: 500;
}

.goods-info-row__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #3b82f6;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  text-decoration: none;
}

.goods-info-row__link:hover {
  color: #2563eb;
}


.goods-price-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 0;
  padding: 18px 0;
  background: #f6f5f5;
  border-radius: 12px;
}

.goods-price-card {
  min-height: 0;
  padding: 0 24px;
  background: transparent;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.goods-price-card:last-child {
  border-right: 0;
}

.goods-price-card__qty {
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: #1a1817;
}

.goods-price-card__price {
  display: flex;
  align-items: baseline;
  gap: 2px;
  margin-top: 4px;
}

.goods-price-card__currency {
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: #ff4600;
}

.goods-price-card__amount {
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  color: #ff4600;
  font-family: Inter, Arial, sans-serif;
}

.goods-price-card__unit {
  margin-top: 2px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: #1a1817;
}

.goods-logistics-flow {
  --logistics-orange: #ff7228;
  --logistics-orange-light: #ffe8d4;
  --logistics-orange-pill: #ffcea8;
  --logistics-bg: #fff5ed;
  --logistics-divider: #ffe8d4;
  --logistics-text: #1a1817;
  --logistics-text-muted: #645a56;
  --logistics-text-light: #908380;
  --logistics-text-faded: #d4cecd;
  --logistics-text-address: #737373;
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #ffead3;
  border-radius: 12px;
  background: #fff5ed;
  overflow: visible;
}

.goods-logistics-flow__progress {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 12px 12px;
  width: 75%;
  margin: 0 auto;
}

.goods-logistics-flow__progress-line {
  flex: 1;
  height: 2px;
  min-width: 40px;
  margin: 0 8px;
  background: #ffe8d4;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.goods-logistics-flow__progress-line::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 6px solid #ffcea8;
}

.goods-logistics-flow__progress-icon {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.goods-logistics-flow__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 10px;
  border-radius: 999px;
  background: #ffcea8;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: #1a1817;
  flex-shrink: 0;
}

.goods-logistics-flow__inspection {
  display: none;
}

.goods-logistics-flow__route {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 12px 7px;
  position: relative;
  width: 95%;
  margin: 0 auto;
  flex-wrap: wrap;
}

.goods-logistics-flow__route-left,
.goods-logistics-flow__route-right {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 14px;
  line-height: 22px;
  flex-shrink: 0;
}

.goods-logistics-flow__route-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-right: 2px;
}

.goods-logistics-flow__route-origin,
.goods-logistics-flow__route-warehouse {
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #1a1817;
  white-space: nowrap;
}

.goods-logistics-flow__route-deliver {
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #908380;
  white-space: nowrap;
  padding: 0 12px;
}

.goods-logistics-flow__route-connector {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 40px;
  position: relative;
}

.goods-logistics-flow__route-line {
  width: 85%;
  height: 1px;
  background: #ffcea8;
  position: relative;
  transform: translateX(10%);
}

.goods-logistics-flow__route-line::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 5px solid #ffcea8;
}

.goods-logistics-flow__inspection-label {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #ff7228;
  white-space: nowrap;
  margin-bottom: 0;
}

.goods-logistics-flow__divider {
  height: 1px;
  margin: 0 12px;
  background: #ffe8d4;
}

.goods-logistics-flow__address-select {
  width: auto;
  min-width: 80px;
}

.goods-logistics-flow__address-select :deep(.el-select__wrapper) {
  padding: 2px 6px;
  background: #fff;
  border: 1px solid #d4cecd;
  border-radius: 4px;
  box-shadow: none;
  cursor: pointer;
}

.goods-logistics-flow__address-select :deep(.el-select__placeholder) {
  color: #737373;
  font-size: 12px;
  line-height: 18px;
}

.goods-logistics-flow__address-select :deep(.el-select__selected-item) {
  color: #1a1817;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
}

.goods-logistics-flow__address-select :deep(.el-select__suffix) {
  color: #908380;
  font-size: 10px;
}

.goods-logistics-flow__address-select :deep(.el-select__caret) {
  font-size: 10px;
}

.goods-logistics-flow__freight {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  width: 90%;
  margin: 0 auto;
}

.goods-logistics-flow__freight-left,
.goods-logistics-flow__freight-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.goods-logistics-flow__freight-label {
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #645a56;
  white-space: nowrap;
}

.goods-logistics-flow__freight-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border: 1px solid #e8e5e5;
  border-radius: 4px;
  background: #fff;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #d4cecd;
}

.goods-logistics-flow__freight-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  color: #3f3b3a;
}
.goods-logistics-flow__freight-icon-wrap:hover {
  color: #ff7228;
}
.goods-logistics-flow__freight-icon--calc {
  cursor: pointer;
  color: inherit;
}
.goods-logistics-flow__freight-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}
.goods-freight-tooltip {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 100;
  filter: drop-shadow(0 12px 8px rgba(26, 24, 23, 0.08)) drop-shadow(0 4px 3px rgba(26, 24, 23, 0.03));
}
.goods-freight-tooltip__arrow {
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 6px solid #fff;
  flex-shrink: 0;
}
.goods-freight-tooltip__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 8px;
  width: 220px;
}
.goods-freight-tooltip__title {
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #1a1817;
  margin: 0;
}
.goods-freight-tooltip__desc {
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #908380;
  margin: 0;
}

.goods-logistics-flow__freight-value {
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #1a1817;
  white-space: nowrap;
}

.goods-service-commitment {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  line-height: 22px;
  flex-wrap: wrap;
}

.goods-service-commitment__icon {
  font-size: 16px;
  color: #22c55e;
  flex-shrink: 0;
}

.goods-service-commitment__label {
  font-weight: 400;
  color: #22c55e;
  white-space: nowrap;
}

.goods-service-commitment__link {
  color: #1a1817;
  cursor: pointer;
  white-space: nowrap;
}

.goods-service-commitment__link:hover {
  color: #ff7228;
}

.goods-service-commitment__assistant {
  display: inline-flex;
  align-items: center;
  position: relative;
  gap: 4px;
  margin-left: auto;
  padding: 4px 10px;
  border-radius: 999px;
  background: #ffe8d2;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
}

.goods-service-commitment__assistant [class*='i-ep'] {
  font-size: 12px;
}

.goods-service-commitment__assistant [class*='i-ep:info-filled'] {
  color: #ff7228;
}

.goods-service-commitment__assistant [class*='i-ep:arrow-down'] {
  color: #908380;
}

.goods-service-commitment__disclaimer-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.goods-service-commitment__disclaimer-arrow {
  font-size: 14px;
  color: #908380;
}

.goods-disclaimer-popup {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  width: 320px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 100;
}

.goods-disclaimer-popup__arrow {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 6px solid #fff;
}

.goods-disclaimer-popup__content {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.goods-disclaimer-popup__title {
  font-size: 12px;
  line-height: 18px;
  font-weight: 700;
  color: #1a1817;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e5e5;
  margin-bottom: 8px;
}

.goods-disclaimer-popup__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
}

.goods-disclaimer-popup__item {
  display: flex;
  gap: 4px;
}

.goods-disclaimer-popup__item-icon {
  font-size: 16px;
  color: #ff7228;
  flex-shrink: 0;
  margin-top: 1px;
}

.goods-disclaimer-popup__item-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.goods-disclaimer-popup__item-title {
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: #1a1817;
}

.goods-disclaimer-popup__item-desc {
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #908380;
}

.goods-assistant-popup {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 88px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 100;
}

.goods-assistant-popup__arrow {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 6px solid #fff;
}

.goods-assistant-popup__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #404040;
  cursor: pointer;
}

.goods-assistant-popup__item:first-child {
  border-radius: 8px 8px 0 0;
}

.goods-assistant-popup__item:last-child {
  border-radius: 0 0 8px 8px;
}

.goods-assistant-popup__item:hover {
  background: #f5f5f5;
}

.goods-assistant-popup__item [class*='i-ep'] {
  font-size: 12px;
  color: #908380;
}

.goods-gallery-actions__right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.goods-share-wrap {
  position: relative;
}

.goods-share-popup {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  width: 108px;
  background: #fff;
  border-radius: 12px;
  box-shadow:
    0 2px 4px -2px rgba(26, 24, 23, 0.06),
    0 4px 8px -2px rgba(26, 24, 23, 0.10);
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 100;
}

.goods-share-popup__item {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  color: #1a1817;
  cursor: pointer;
  white-space: nowrap;
}

.goods-share-popup__item--active {
  background: #fff5ed;
}

.goods-share-popup__item:hover {
  background: #f5f5f5;
}

.goods-share-popup__item--active:hover {
  background: #ffead3;
}

.goods-share-popup__icon {
  position: relative;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.goods-share-popup__icon-bg {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: #ff7228;
}

.goods-share-popup__icon-bg--line {
  background: #06c755;
}

.goods-share-popup__icon-bg--wechat {
  background: #07c160;
}

.goods-share-popup__icon-inner {
  position: relative;
  z-index: 1;
  color: #fff;
  font-size: 12px;
}

.goods-panel-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  background: transparent;
  border: 0;
}

.goods-spec-group {
  /* display: grid;
  grid-template-columns: 88px minmax(0, 1fr); */
  gap: 16px;
  align-items: start;
}

.goods-spec-group__label {
  margin: 8px 0 0;
  color: var(--goods-text-muted);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: normal;
  word-break: break-word;
}

.goods-spec-values {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.goods-spec-value {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 46px;
  padding: 6px 14px 6px 6px;
  border-radius: 6px;
  border: 1px solid var(--goods-border-strong);
  background: var(--goods-surface);
  color: var(--goods-text);
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
  font-size: 12px;
  line-height: 16px;
}

.goods-spec-value__image {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: zoom-in;
}

.goods-spec-value__image :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.goods-spec-value.is-selected {
  border: 2px solid #ff7228;
  padding: 5px 13px 5px 5px;
  color: #e55d1a;
  box-shadow: none;
}

.goods-quantity-table {
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}

.goods-quantity-table__head,
.goods-quantity-row {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) 110px 92px 104px;
  align-items: center;
}

.goods-quantity-table__head {
  background: #f6f5f5;
  color: #1a1817;
  font-size: 15px;
  line-height: 26px;
  font-weight: 500;
  padding: 10px 16px;
}

.goods-quantity-row {
  padding: 12px 16px;
  min-height: 56px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  gap: 12px;
}

.goods-quantity-table__toggle {
  width: 100%;
  height: 36px;
  border: 0;
  background: #f6f5f5;
  color: #ff7228;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.goods-quantity-row__spec {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.goods-quantity-row__content {
  min-width: 0;
}

.goods-quantity-row__image {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: zoom-in;
}

.goods-quantity-row__image :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.goods-quantity-row__title {
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #1a1817;
  white-space: normal;
  word-break: break-word;
}

.goods-quantity-row__sub {
  margin-top: 2px;
  font-size: 12px;
  line-height: 18px;
  color: #908380;
}

.goods-quantity-row__price {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  font-family: Inter, Arial, sans-serif;
  color: #1a1817;
  white-space: nowrap;
}

.goods-quantity-row__stock {
  color: #908380;
  font-size: 14px;
  line-height: 20px;
  font-family: Inter, Arial, sans-serif;
  white-space: nowrap;
}

.goods-stepper {
  display: inline-flex;
  align-items: center;
  justify-self: start;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  overflow: hidden;
  background: #ffffff;
}

.goods-stepper__button {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: #1a1817;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.goods-stepper__button:hover {
  background: rgba(0, 0, 0, 0.04);
  color: #ff7228;
}

.goods-stepper__input {
  width: 56px;
  height: 32px;
  border: 0;
  border-left: 1px solid rgba(0, 0, 0, 0.06);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  text-align: center;
  color: #1a1817;
  font-weight: 600;
  background: transparent;
  font-size: 14px;
}

.goods-disclaimer {
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.goods-disclaimer p {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: #645a56;
}

.goods-agreement {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #1a1817;
}

.goods-disclaimer__chevron {
  font-size: 16px;
  color: #908380;
  margin-left: auto;
  cursor: pointer;
}

.goods-payment {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 0;
}

.goods-payment__label {
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: #1a1817;
}

.goods-payment__icons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.goods-payment__icon {
  height: 24px;
  width: 24px;
  display: block;
}

.goods-price-summary {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 0;
  padding-top: 12px;
  border-top: 1px solid #e8e5e5;
}

.goods-price-summary__left {
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: #1a1817;
}

.goods-price-summary__right {
  display: flex;
  align-items: flex-end;
  gap: 0;
}

.goods-price-summary__row {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.goods-price-summary__row-label {
  font-size: 13px;
  line-height: 20px;
  color: #1a1817;
  margin-right: 0;
  margin-left: 12px;
}

.goods-price-summary__row-currency {
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: #ff4600;
}

.goods-price-summary__row-amount {
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  color: #ff4600;
  font-family: Inter, Arial, sans-serif;
}

.goods-price-summary__row-amount.is-muted {
  font-weight: 600;
  color: #645a56;
}

.goods-price-summary__row-separator {
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: #1a1817;
}

.goods-price-summary__row-unit {
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: #1a1817;
}

.goods-cta-row {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 0;
}

.theme-button {
  min-width: 0;
  height: 40px;
  padding: 10px 20px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 0 0 auto;
}

.theme-button--primary {
  border: 0;
  background: #ff7228;
  color: #fff;
}

.theme-button--primary:hover {
  background: #e55d1a;
}

.theme-button--secondary {
  border: 1px solid #ff7228;
  background: #fff;
  color: #ff7228;
}

.theme-button--secondary:hover {
  background: #fff5ed;
}

.theme-bookmark-button {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 46px;
  height: 40px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #908380;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.theme-bookmark-button:hover {
  color: #ff7228;
}

.goods-store-section {
  margin-top: 12px;
  background: #fff;
  border: 1px solid #e8e5e5;
  border-radius: 12px;
  overflow: hidden;
}

.goods-store-section__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #fff6ec;
}

.goods-store-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #ffe8d4;
  border: 1px solid #ffcea8;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #9d240e;
}

.goods-store-tag.is-sm {
  gap: 4px;
  padding: 2px 8px;
  font-size: 14px;
  line-height: 20px;
}

.goods-store-tag__icon {
  font-size: 16px;
}

.goods-store-enter-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 0;
  border-radius: 2px;
  background: #ff4600;
  color: #fff;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.goods-store-enter-btn:hover {
  background: #e55d1a;
}

.goods-store-info {
  display: flex;
  align-items: flex-start;
  gap: 36px;
  padding: 16px 20px;
}

.goods-store-info__logo {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  border: 1px solid #e8e5e5;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f5f5;
}

.goods-store-info__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.goods-store-info__details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.goods-store-info__row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.goods-store-info__label {
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #645a56;
  white-space: nowrap;
  min-width: 84px;
}

.goods-store-info__value {
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #1a1817;
}

.goods-store-info__value.is-name {
  font-weight: 500;
}

.goods-store-info__value.is-rating {
  font-family: Inter, Arial, sans-serif;
  font-weight: 500;
}

.goods-store-info__value.is-metric {
  font-family: Inter, Arial, sans-serif;
  font-weight: 400;
  color: #1a1817;
}

.goods-store-stars {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 14px;
  color: #d4cecd;
}

.goods-store-stars .is-filled {
  color: #f59e0b;
}

.goods-store-info__divider {
  width: 1px;
  align-self: stretch;
  background: #e8e5e5;
  flex-shrink: 0;
}

.goods-store-info__ratings {
  background: rgba(255,245,237,0.5);
  border-radius: 8px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.goods-store-info__rating-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.goods-store-info__rating-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px;
}

.goods-store-info__rating-label {
  font-size: 13px;
  color: #1a1817;
  line-height: 20px;
}

.goods-store-info__rating-score {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #1a1817;
  line-height: 24px;
}

.goods-store-info__rating-level {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
}

.goods-store-info__rating-level.high {
  color: #FF6B35;
}

.goods-store-info__rating-level.mid {
  color: #FFA500;
}

.goods-store-info__rating-level.low {
  color: #999;
}

.goods-store-info__metrics {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.goods-store-verification {
  padding: 0 20px 16px;
}

.goods-store-verification__title {
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: #1a1817;
  margin-bottom: 8px;
}

.goods-store-verification__images {
  display: flex;
  gap: 20px;
  overflow-x: auto;
}

.goods-store-verification__image {
  width: 100px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f6f5f5;
}

.goods-store-verification__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.goods-store-verification__empty {
  width: 100%;
  text-align: center;
  color: #908380;
  font-size: 13px;
  line-height: 80px;
}

.goods-content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  margin-top: 24px;
  align-items: start;
}

.goods-detail-card {
  background: var(--goods-surface);
  border: 1px solid var(--goods-border-soft);
  /* border-radius: 12px; */
  box-shadow: var(--goods-shadow);
}

.goods-detail-card {
  overflow: hidden;
}

.goods-detail-panel {
  padding: 40px;
}

.goods-detail-intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 0 0;
  text-align: center;
}

.goods-detail-intro__eyebrow,
.goods-native-description__eyebrow {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.3em;
  color: #ff7228;
  font-family: Manrope, Arial, sans-serif;
}

.goods-detail-intro h2 {
  max-width: 480px;
  margin: 0;
  font-size: 32px;
  line-height: 38px;
  color: #1a1817;
  font-weight: 400;
  font-family: "Noto Serif SC", "Source Han Serif SC", "Times New Roman", serif;
}

.goods-detail-intro p {
  max-width: 576px;
  margin: 0;
  color: #645a56;
  font-size: 14px;
  line-height: 24px;
}

.goods-parameter-section {
  padding-bottom: 32px;
  margin-bottom: 32px;
  border-bottom: 1px solid var(--goods-border-soft);
}

.goods-parameter-section__title {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 700;
  color: #212121;
}

.goods-parameter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 64px;
}

.goods-parameter-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 7px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.goods-parameter-item__label {
  flex-shrink: 0;
  min-width: 96px;
  color: #1a1817;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
}

.goods-parameter-item__value {
  flex: 1;
  color: #1a1817;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  word-break: break-word;
}

.goods-visual-showcase {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.goods-visual-showcase__main {
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.1);
}

.goods-visual-showcase__main img {
  width: 100%;
  display: block;
  object-fit: cover;
  aspect-ratio: 1 / 1;
}

.goods-visual-showcase__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 32px;
}

.goods-visual-feature-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.goods-visual-feature-card__image {
  border-radius: 16px;
  overflow: hidden;
  background: var(--goods-surface-deep);
}

.goods-visual-feature-card__image img {
  width: 100%;
  display: block;
  object-fit: cover;
  aspect-ratio: 1 / 1;
}

.goods-visual-feature-card__body {
  padding: 0 8px;
}

.goods-visual-feature-card__body h3 {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--goods-text);
}

.goods-visual-feature-card__body p {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 19.5px;
  color: var(--goods-text-muted);
}

.goods-eco-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px;
  border-radius: 16px;
  background: var(--goods-surface-muted);
  text-align: center;
}

.goods-eco-card__icon {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--goods-surface);
  color: var(--goods-primary);
  font-size: 22px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}

.goods-eco-card__title {
  margin-top: 24px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--goods-text);
}

.goods-eco-card__desc {
  max-width: 672px;
  margin-top: 24px;
  font-size: 14px;
  line-height: 28px;
  color: var(--goods-text-muted);
}

.goods-native-description {
  margin-top: 48px;
  padding-top: 40px;
  border-top: 1px solid var(--goods-border-soft);
}

.goods-native-description__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  text-align: center;
}

.goods-native-description__head h3 {
  margin: 0;
  font-size: 22px;
  line-height: 30px;
  font-weight: 600;
  color: var(--goods-text);
}

.goods-copy-panel h6 {
  margin: 24px 0 8px;
  font-size: 16px;
  line-height: 1.75;
  font-weight: 700;
  color: var(--goods-text);
}

.goods-copy-panel h6:first-child {
  margin-top: 0;
}

.goods-copy-panel p {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.9;
  color: var(--goods-text);
}

.goods-copy-panel .is-muted {
  color: var(--goods-text-muted);
}

.goods-copy-panel__images {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 12px 0 8px;
}

.goods-copy-panel__images img {
  width: 120px;
  height: 120px;
  border-radius: 12px;
  object-fit: cover;
}

.goods-copy-panel__images--wide img {
  width: 120px;
  height: 120px;
}

.goods-service-copy {
  padding: 20px 24px;
  border-radius: 16px;
  background: var(--goods-surface-muted);
}

:deep(.description-html) {
  width: 100%;
  overflow-x: auto;
  color: var(--goods-text);
  text-align: center;
}

:deep(.description-html div) {
  width: 1160px !important;
  max-width: 100% !important;
}

:deep(.description-html img) {
  display: block;
  margin: 0 auto;
  max-width: 100% !important;
}

.goods-detail-tabs :deep(.el-tabs__header) {
  margin-bottom: 20px;
  padding: 0;
}

.goods-detail-tabs :deep(.el-tabs__nav-wrap) {
  padding: 0;
}

.goods-detail-tabs :deep(.el-tabs__nav-scroll) {
  padding: 0;
}

.goods-detail-tabs :deep(.el-tabs__nav) {
  float: none;
  display: flex;
  width: 100%;
  gap: 0;
}

.goods-detail-tabs :deep(.el-tabs__nav-wrap::after) {
  display: none;
}

.goods-detail-tabs :deep(.el-tabs__item) {
  height: 44px;
  line-height: 44px;
  padding: 0 20px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Noto Sans TC', sans-serif;
  color: #1a1817;
  background: #f6f5f5;
  border-radius: 0;
  border: none;
  transition: all 0.15s ease;
}

.goods-detail-tabs :deep(.el-tabs__item:first-child) {
  padding-left: 20px;
}

.goods-detail-tabs :deep(.el-tabs__item:last-child) {
  padding-right: 20px;
}

.goods-detail-tabs :deep(.el-tabs__item.is-active) {
  font-weight: 600;
  color: #fff;
  background: linear-gradient(to bottom, #ff7228, #fe5911);
}

.goods-detail-tabs :deep(.el-tabs__active-bar) {
  display: none;
}

.el-textarea :deep(.el-textarea__inner) {
  min-height: 72px;
  border-radius: 8px;
  border-color: var(--goods-border);
  background: rgba(255, 255, 255, 0.96);
  color: var(--goods-text);
  box-shadow: none;
}

.el-textarea :deep(.el-textarea__inner:focus) {
  border-color: var(--goods-primary);
}

@media (max-width: 1024px) {

  .goods-hero,
  .goods-content-grid {
    grid-template-columns: 1fr;
  }


}

@media (max-width: 900px) {
  .goods-detail-page__container {
    width: min(calc(100vw - 24px), 1188px);
    padding: 20px 0 32px;
  }

  .goods-hero,
  .goods-detail-panel {
    padding: 24px;
  }

  .goods-price-grid {
    grid-template-columns: 1fr;
    padding: 8px 0;
  }

  .goods-price-card {
    padding: 12px 16px;
    border-right: 0;
    border-bottom: 1px solid var(--goods-border);
  }

  .goods-price-card:last-child {
    border-bottom: 0;
  }

  .goods-spec-group {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .goods-spec-group__label {
    margin-top: 0;
  }

  .goods-quantity-table__head,
  .goods-quantity-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .goods-quantity-table__head {
    display: none;
  }

  .goods-quantity-row {
    gap: 10px;
  }


  .goods-parameter-grid,
  .goods-visual-showcase__grid {
    grid-template-columns: 1fr;
  }


  .goods-cta-row {
    flex-wrap: wrap;
  }

  .theme-button {
    flex: 1 1 calc(50% - 6px);
    min-width: 0;
  }

  .goods-eco-card {
    padding: 32px 20px;
  }

  .goods-detail-intro h2 {
    font-size: 28px;
    line-height: 34px;
  }

  .goods-detail-intro p,
  .goods-eco-card__desc {
    font-size: 14px;
    line-height: 24px;
  }
}
</style>

<style>
.goods-logistics-flow__address-popper {
  border-radius: 8px !important;
  border: 1px solid #ffe8d4 !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06) !important;
  padding: 4px 0 !important;
  min-width: 120px !important;
}

.goods-logistics-flow__address-popper .el-select-dropdown__item {
  padding: 6px 14px;
  font-size: 12px;
  line-height: 18px;
  color: #1a1817;
  border-radius: 0;
}

.goods-logistics-flow__address-popper .el-select-dropdown__item.is-selected {
  color: #ff7228;
  font-weight: 600;
  background: #fff5ed;
}

.goods-logistics-flow__address-popper .el-select-dropdown__item:hover {
  background: #fff5ed;
}
.i-ep\:shop,.i-ep\:link,.orange-text{
  color: orange;
}
.tab-goods-detail-panel{
  padding: 0 40px;
}

/* ======= 加入购物车成功弹窗 (Figma design) ======= */
.cart-success-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.cart-success-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 12px;
  position: relative;
  width: 400px;
  overflow: hidden;
}

.cart-success-modal__content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  width: 100%;
}

.cart-success-modal__icon {
  background: #dcfce7;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  font-size: 16px;
  color: #16a34a;
}

.cart-success-modal__text-area {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.cart-success-modal__title {
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  color: #1a1817;
  margin: 0;
}

.cart-success-modal__link {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #3b82f6;
  margin: 0;
  cursor: pointer;
}

.cart-success-modal__link:hover {
  text-decoration: underline;
}

.cart-success-modal__button {
  background: #ff7228;
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  padding: 10px 20px;
  white-space: nowrap;
}

.cart-success-modal__button:hover {
  background: #e66520;
}

.cart-success-modal__divider {
  position: absolute;
  top: 84px;
  left: 0;
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
}
</style>
<style lang="scss" scoped>
::v-deep .el-tabs--bottom>.el-tabs__header .el-tabs__item:nth-child(2),::v-deep  .el-tabs--top>.el-tabs__header .el-tabs__item:nth-child(2){
  padding-left: 20px;
}
</style>
